import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert, Table,
  Nav, NavItem, NavLink, TabContent, TabPane
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import KullaniciIletisimModal from "../../../components/Common/KullaniciIletisimModal";
import TableContainer from "../../../components/Common/TableContainer";
import { matchesSearch } from "../../../helpers/search";
import {
  getConversationsByAdvert,
  getConversationMessages,
} from "../../../helpers/chatFirebase";
import {
  parseApiDate,
  formatApiDate,
  formatApiDateTime,
  formatApiDateShort,
} from "../../../helpers/apiDate";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

// DURUM tinyint helpers (ILAN_TEKLIFLERI)
const teklifDurumText = d =>
  d === 1 ? "Kabul Edildi" : d === 2 ? "Reddedildi" : "Bekliyor";
const teklifDurumColor = d =>
  d === 1 ? "success" : d === 2 ? "danger" : "warning";

// Para birimi (ILANLAR.PARA_BIRIMI) — TRY | EUR
const normalizeCurrency = (raw) => {
  if (!raw || typeof raw !== "string") return "TRY";
  const u = raw.trim().toUpperCase();
  if (u === "EUR" || u === "EURO" || u === "€") return "EUR";
  return "TRY";
};
const currencySymbol = (raw) => (normalizeCurrency(raw) === "EUR" ? "€" : "₺");
const formatIlanPrice = (value, currency) => {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (Number.isNaN(n)) return "-";
  return `${n.toLocaleString("tr-TR")} ${currencySymbol(currency)}`;
};


// İlan düzenlenmiş mi? (güncelleme tarihi, oluşturmadan en az 1 dk sonra ise
// satıcı ilanı düzenlemiş demektir → yeniden onay gerekir)
const isDuzenlendi = (i) => {
  if (!i?.GUNCELLEME_TARIHI || !i?.OLUSTURMA_TARIHI) return false;
  const g = parseApiDate(i.GUNCELLEME_TARIHI)?.getTime();
  const o = parseApiDate(i.OLUSTURMA_TARIHI)?.getTime();
  if (g == null || o == null) return false;
  if (Number.isNaN(g) || Number.isNaN(o)) return false;
  return g - o > 60 * 1000;
};

// API datetime kolonları UTC yazılıyor ama OData bunları SUNUCUNUN yerel saat
// dilimiyle etiketleyerek döndürüyor ("2026-08-14T17:17:46+03:00" — rakamlar UTC,
// etiket TR). Etikete güvenilirse tüm saatler tam 3 saat geriden görünür.
// parseApiDate etiketi atıp değeri UTC kabul eder; ayrıntı: helpers/apiDate.js
const parseUtc = parseApiDate;

// ─── SIRALAMA ───────────────────────────────────────────────────
// Sıralama tablo bileşeninde değil sayfada yapılır: sayfalama, toplu seçim ve
// filtreler aynı sıralı listeyi görür. Boş değerler (sınırsız stok, fiyatsız
// ilan) yön ne olursa olsun en sona düşer, yoksa "çoktan aza" listesinin başını
// ∞ stoklu ilanlar doldururdu.
const SIRALAMA_SECENEKLERI = [
  { deger: "tarih:desc", etiket: "En yeni ilan" },
  { deger: "tarih:asc", etiket: "En eski ilan" },
  { deger: "goruntulenme:desc", etiket: "Görüntülenme (çoktan aza)" },
  { deger: "goruntulenme:asc", etiket: "Görüntülenme (azdan çoka)" },
  { deger: "stok:desc", etiket: "Stok (çoktan aza)" },
  { deger: "stok:asc", etiket: "Stok (azdan çoka)" },
  { deger: "fiyat:desc", etiket: "Fiyat (yüksekten düşüğe)" },
  { deger: "fiyat:asc", etiket: "Fiyat (düşükten yükseğe)" },
  { deger: "teklif:desc", etiket: "Teklif (çoktan aza)" },
  { deger: "teklif:asc", etiket: "Teklif (azdan çoka)" },
  { deger: "ad:asc", etiket: "İlan adı (A → Z)" },
  { deger: "ad:desc", etiket: "İlan adı (Z → A)" },
];
const VARSAYILAN_SIRALAMA = { alan: "tarih", yon: "desc" };

// ─── SİTEDEKİ İLAN LİNKİ ────────────────────────────────────────
// Mobil paylaşım linkiyle birebir aynı (ProductDetailScreen toSlug). Web sayfası
// yalnız sondaki ID'yi okur; slug okunabilirlik için. Değerleme ilanları ayrı sayfada.
const ilanSlug = (text) =>
  (text || "")
    .toLowerCase()
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

const ilanWebLinki = (i) => {
  if (!i?.ID) return null;
  if (i.DEGERLEMEYE_ACIK_MI) return `https://harmanagel.com/degerleme/${i.ID}`;
  const slug = ilanSlug(i.AD);
  return `https://harmanagel.com/harman-satin-al/${slug ? `${slug}-` : ""}${i.ID}`;
};

const linkiKopyala = async (url) => {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    window.prompt("Linki kopyalayın:", url);
    return false;
  }
};

const siralamaDegeri = (i, alan, teklifSayilari) => {
  switch (alan) {
    case "goruntulenme": return i.GÖRÜNTÜLENME_SAYISI ?? 0;
    case "stok": return i.STOK_ADEDI ?? null;
    case "fiyat": {
      const ind = Number(i.INDIRIMLI_FIYAT);
      if (ind > 0) return ind;
      const f = Number(i.FIYAT);
      return f > 0 ? f : null;
    }
    case "teklif": return teklifSayilari[i.ID] || 0;
    case "ad": return (i.AD || "").trim() || null;
    default: return parseApiDate(i.OLUSTURMA_TARIHI)?.getTime() ?? null;
  }
};

// Sütun başlığı: tıklayınca önce "çoktan aza" (ad için A→Z), tekrar tıklayınca ters.
const SiraliBaslik = ({ etiket, alan, siralama, setSiralama, ilkYon = "desc" }) => {
  const aktif = siralama.alan === alan;
  const ikon = !aktif ? "bx-sort-alt-2" : siralama.yon === "desc" ? "bx-sort-down" : "bx-sort-up";
  return (
    <button
      type="button"
      className={`btn btn-link p-0 border-0 text-decoration-none d-inline-flex align-items-center gap-1 fw-semibold ${aktif ? "text-primary" : "text-body"}`}
      style={{ whiteSpace: "nowrap", fontSize: "inherit" }}
      onClick={() =>
        setSiralama(aktif
          ? { alan, yon: siralama.yon === "desc" ? "asc" : "desc" }
          : { alan, yon: ilkYon })
      }
      title={aktif ? "Sıralama yönünü değiştir" : `${etiket} göre sırala`}
    >
      {etiket}
      <i className={`bx ${ikon} font-size-16 ${aktif ? "" : "text-muted opacity-50"}`} />
    </button>
  );
};

// İlan şu an gerçekten vitrinde mi? Süresi dolmuş premium ilan vitrinde sayılmaz
// (HomeScreen sorgusu VITRIN_BITIS_TARIHI gt now şartını arıyor).
const vitrindeMi = (i) => {
  if (!i?.IsPremium) return false;
  const bitis = parseUtc(i.VITRIN_BITIS_TARIHI);
  return !bitis || bitis.getTime() > Date.now();
};

// Premium ilanın vitrin durumunu rozet etiketi + tooltip olarak verir.
// HomeScreen'deki vitrin sorgusu "VITRIN_BITIS_TARIHI gt now OR eq null" olduğu
// için tarih boş bırakılan ilan vitrinden hiç düşmez — bunu görünür kılıyoruz.
const vitrinDurumu = (i) => {
  if (!i?.IsPremium) return { etiket: "", baslik: "" };
  const bitis = parseUtc(i.VITRIN_BITIS_TARIHI);
  if (!bitis) {
    return {
      etiket: " · süresiz",
      baslik: "Vitrin bitiş tarihi girilmemiş — ilan vitrinde süresiz kalır.",
    };
  }
  const kalanMs = bitis.getTime() - Date.now();
  if (kalanMs <= 0) {
    return {
      etiket: " · doldu",
      baslik: `Vitrin süresi doldu (${bitis.toLocaleString("tr-TR")}). İlan vitrinde görünmüyor.`,
    };
  }
  const kalanGun = Math.ceil(kalanMs / (24 * 60 * 60 * 1000));
  return {
    etiket: ` · ${kalanGun}g`,
    baslik: `Vitrin bitişi: ${bitis.toLocaleString("tr-TR")}`,
  };
};

const IlanYonetimi = () => {
  document.title = "İlan Yönetimi | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  // ─── DATA ────────────────────────────────────────────────────
  const [ilanlar, setIlanlar] = useState([]);
  const [kategoriler, setKategoriler] = useState([]);
  // Kategori hiyerarşisi (üst/alt) KATEGORI_BAGLANTILARI ara tablosunda tutulur.
  const [baglantiler, setBaglantiler] = useState([]);
  const [teklifSayilari, setTeklifSayilari] = useState({}); // { ilanId: count }
  // Alt kategorisi olan bir kategoride duran ("boşta kalan") ilanlar. Ana liste
  // yalnızca son 1000 ilanı yüklediği için eskileri kaçmasın diye sunucudan ayrıca
  // çekilir. Ör. traktör marka düğümünde kalıp modele taşınmamış ilanlar.
  const [bostaIlanlar, setBostaIlanlar] = useState([]);
  // EİDS geçiş dönemi: e-Devlet yetki doğrulaması gelene kadar taşınmaz / araç /
  // traktör ilanı yayına alınmamalı (mevzuat + Bakanlık cezası). Kök kategori
  // listesi AYARLAR.EIDS_ENGELLI_KATEGORILER'den okunur; alt ağaç dahildir.
  const [eidsKokler, setEidsKokler] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  // ─── FILTERS ─────────────────────────────────────────────────
  const [filterDurum, setFilterDurum] = useState("all");
  const [filterKategori, setFilterKategori] = useState("all");
  const [filterTip, setFilterTip] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateStart, setDateStart] = useState("");       // yyyy-mm-dd (oluşturma tarihi başlangıç)
  const [dateEnd, setDateEnd] = useState("");            // yyyy-mm-dd (oluşturma tarihi bitiş)
  const [datePreset, setDatePreset] = useState("all");  // hızlı aralık seçimi
  const [siralama, setSiralama] = useState(VARSAYILAN_SIRALAMA);

  // ─── BULK SELECT ─────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // ─── SİLİNENLER GÖRÜNÜMÜ ─────────────────────────────────────
  // Soft-delete: silinen ilanlar DB'den silinmez, AKTIF_DURUM='Silindi' olur.
  // Aktif listede gösterilmez; bu görünümde ayrı listelenir. Mobil public
  // listeler zaten AKTIF_DURUM='Aktif' filtrelediği için orada görünmezler.
  const [showDeleted, setShowDeleted] = useState(false);

  // ─── DETAIL MODAL ─────────────────────────────────────────────
  const [detailModal, setDetailModal] = useState(false);
  const [detailIlan, setDetailIlan] = useState(null);
  const [detailTab, setDetailTab] = useState("1");
  const [detailLoading, setDetailLoading] = useState(false);
  const [ilanMedya, setIlanMedya] = useState([]);
  const [ilanTeklifler, setIlanTeklifler] = useState([]);
  const [ilanDegerlemeler, setIlanDegerlemeler] = useState([]);
  const [ilanSikayetler, setIlanSikayetler] = useState([]);

  // ─── MESAJLAŞMALAR (Firestore) ────────────────────────────────
  // Mobil uygulamadaki kullanıcı-kullanıcı sohbeti Firestore'da tutulur.
  // İlana ait sohbetler advertID ile eşleştirilir; admin salt-okunur görür.
  const [ilanSohbetler, setIlanSohbetler] = useState([]);
  const [sohbetLoading, setSohbetLoading] = useState(false);
  const [sohbetYuklendi, setSohbetYuklendi] = useState(false); // bu ilan için sohbetler çekildi mi
  const [sohbetError, setSohbetError] = useState("");
  const [aktifSohbet, setAktifSohbet] = useState(null);
  const [sohbetMesajlar, setSohbetMesajlar] = useState([]);
  const [mesajLoading, setMesajLoading] = useState(false);
  const [sohbetAra, setSohbetAra] = useState("");   // sohbet listesi araması
  const [mesajAra, setMesajAra] = useState("");     // sohbet içi mesaj araması

  // ─── EDIT MODAL ───────────────────────────────────────────────
  const [editModal, setEditModal] = useState(false);
  const [editIlan, setEditIlan] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editKatSearch, setEditKatSearch] = useState(""); // düzenleme modalı kategori araması
  const [editKatDropdown, setEditKatDropdown] = useState(false);

  // ─── FETCH ───────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [ilanRes, katRes, bagRes, teklifRes] = await Promise.all([
        fetch(
          // TÜM ilanlar (API MaxTop=50000). Eskiden $top=1000'di: kartlardaki
          // Toplam/Aktif/Pasif sayıları yalnız son 1000 ilandan hesaplanıyor,
          // Dashboard'daki (tüm ilanlar) sayılarla tutmuyordu; eski ilanlar
          // filtrelerde de çıkmıyordu. ~2.250 ilan ≈ 3 MB / 3-4 sn (2026-10).
          `${BASE_URL}/ILANLAR?$top=50000&$orderby=OLUSTURMA_TARIHI desc` +
          `&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress,CEP_NO),KATEGORILER($select=AD,ID)`,
          { headers }
        ),
        // $top: traktör modelleriyle kategori sayısı 1000'i aşıyor; sayfalanırsa
        // yol (Ana › … › Diğer) yarıda kopuyordu.
        fetch(`${BASE_URL}/KATEGORILER?$orderby=AD asc&$select=ID,AD&$top=5000`, { headers }),
        fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI?$top=10000`, { headers }),
        fetch(`${BASE_URL}/ILAN_TEKLIFLERI?$select=ID,ILAN_ID,DURUM&$top=50000`, { headers }),
      ]);

      const [ilanData, katData, bagData, teklifData] = await Promise.all([
        ilanRes.json(), katRes.json(), bagRes.json(), teklifRes.json(),
      ]);

      const ilanList = ilanData.value || [];
      const teklifList = teklifData.value || [];

      // Teklif sayısı per ilan
      const counts = {};
      teklifList.forEach(t => {
        counts[t.ILAN_ID] = (counts[t.ILAN_ID] || 0) + 1;
      });

      // EİDS kökleri: ayrı ve hatası yutulan istek — okunamazsa liste bozulmasın.
      fetch(`${BASE_URL}/AYARLAR?$filter=ANAHTAR eq 'EIDS_ENGELLI_KATEGORILER'&$select=DEGER`, { headers })
        .then(r => (r.ok ? r.json() : null))
        .then(d => {
          const deger = d?.value?.[0]?.DEGER || "";
          setEidsKokler(deger.split(/[,;\s]+/).map(x => parseInt(x, 10)).filter(n => !Number.isNaN(n)));
        })
        .catch(() => {});

      setIlanlar(ilanList);
      setKategoriler(katData.value || []);
      setBaglantiler(bagData.value || []);
      setTeklifSayilari(counts);

      // Boşta kalanlar: KATEGORI_ID alt kategorisi olan bir kategori. ID listesi
      // parçalı gönderilir — tek sorguda IIS'in 2048 karakter URL sınırı aşılıyor.
      const ustIds = [...new Set((bagData.value || []).map(b => b.UST_KATEGORI_ID))];
      const PARCA = 100;
      const parcalar = [];
      for (let i = 0; i < ustIds.length; i += PARCA) parcalar.push(ustIds.slice(i, i + PARCA));
      const bostaSonuclar = await Promise.all(parcalar.map(async ids => {
        const filtre = encodeURIComponent(
          `KATEGORI_ID in (${ids.join(",")}) and AKTIF_DURUM ne 'Silindi'`
        );
        const r = await fetch(
          `${BASE_URL}/ILANLAR?$filter=${filtre}&$orderby=OLUSTURMA_TARIHI desc` +
          `&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress,CEP_NO),KATEGORILER($select=AD,ID)`,
          { headers }
        );
        if (!r.ok) return [];
        const j = await r.json();
        return j.value || [];
      }));
      setBostaIlanlar(bostaSonuclar.flat());
    } catch {
      setError("Veriler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── SUNUCU ARAMASI ──────────────────────────────────────────
  // Liste artık tüm ilanları yüklüyor; sunucu araması yedek olarak kalır (ilk yükleme
  // bitmeden yazılan arama ve ileride liste yeniden sınırlanırsa). Eskiden daha eski bir ilan (ör. E-Ahırım/kurbanlık)
  // aramada hiç çıkmıyordu, bulunup silinemiyordu. Arama yazılınca başlık/ID ile
  // TÜM ilanlarda sunucuda aranır, sonuçlar yüklü listeye eklenir.
  const [aramaSonuclari, setAramaSonuclari] = useState([]);
  const [aramaYukleniyor, setAramaYukleniyor] = useState(false);
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) { setAramaSonuclari([]); return undefined; }
    let iptal = false;
    const t = setTimeout(async () => {
      setAramaYukleniyor(true);
      try {
        const kacisli = q.replace(/'/g, "''");
        const no = /^#?\d+$/.test(q) ? parseInt(q.replace("#", ""), 10) : null;
        const filtre = no != null
          ? `ID eq ${no} or contains(AD, '${kacisli}')`
          : `contains(AD, '${kacisli}')`;
        const res = await fetch(
          `${BASE_URL}/ILANLAR?$filter=${encodeURIComponent(filtre)}&$top=200&$orderby=OLUSTURMA_TARIHI desc` +
          `&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress,CEP_NO),KATEGORILER($select=AD,ID)`,
          { headers }
        );
        if (!res.ok) return;
        const j = await res.json();
        if (!iptal) setAramaSonuclari(j.value || []);
      } catch {
        /* sessiz: yerel arama yine çalışır */
      } finally {
        if (!iptal) setAramaYukleniyor(false);
      }
    }, 400);
    return () => { iptal = true; clearTimeout(t); };
  }, [searchQuery, headers]);

  // Alarm polling'i yeni ilan/değerleme tespit ettiğinde sayfadayken listeyi otomatik yenile
  useEffect(() => {
    const onNewEvent = (e) => {
      const type = e.detail?.type;
      if (type === "VALUATION" || type === "AD") {
        fetchAll();
      }
    };
    window.addEventListener("pending-alarm:new-event", onNewEvent);
    return () => window.removeEventListener("pending-alarm:new-event", onNewEvent);
  }, [fetchAll]);

  // ─── DETAIL FETCH ─────────────────────────────────────────────
  // Genel sekmedeki önizlemede seçili fotoğraf (ilanMedya içindeki sıra)
  const [onizlemeIdx, setOnizlemeIdx] = useState(0);
  // İlan/değerleme sahibine uyarı bildirimi / arama
  const [iletisimIlan, setIletisimIlan] = useState(null);

  const openDetail = async (ilan) => {
    setOnizlemeIdx(0);
    setDetailIlan(ilan);
    setDetailTab("1");
    setDetailModal(true);
    setDetailLoading(true);
    setIlanMedya([]);
    setIlanTeklifler([]);
    setIlanDegerlemeler([]);
    setIlanSikayetler([]);
    setIlanSohbetler([]);
    setSohbetYuklendi(false);
    setAktifSohbet(null);
    setSohbetMesajlar([]);
    setSohbetError("");
    setSohbetAra("");
    setMesajAra("");
    try {
      const [mRes, tRes, dRes, sRes] = await Promise.all([
        fetch(`${BASE_URL}/ILANLAR_MEDYA?$filter=ILAN_ID eq ${ilan.ID}&$orderby=SIRA asc`, { headers }),
        fetch(`${BASE_URL}/ILAN_TEKLIFLERI?$filter=ILAN_ID eq ${ilan.ID}&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress)&$orderby=TEKLIF_TARIHI desc`, { headers }),
        fetch(`${BASE_URL}/ILAN_DEGERLEMELERI?$filter=ILAN_ID eq ${ilan.ID}&$expand=KULLANICILAR($select=AD_SOYAD)&$orderby=TARIH desc`, { headers }),
        fetch(`${BASE_URL}/ILAN_SIKAYETLERI?$filter=ILAN_ID eq ${ilan.ID}&$expand=KULLANICILAR($select=AD_SOYAD)&$orderby=TARIH desc`, { headers }),
      ]);
      const [mData, tData, dData, sData] = await Promise.all([
        mRes.json(), tRes.json(), dRes.json(), sRes.json(),
      ]);
      setIlanMedya(mData.value || []);
      setIlanTeklifler(tData.value || []);
      setIlanDegerlemeler(dData.value || []);
      setIlanSikayetler(sData.value || []);
    } catch { }
    finally { setDetailLoading(false); }
  };

  // ─── DERİN BAĞLANTI: /ilan-yonetimi?ilan=123 ──────────────────
  // Mesajlaşmalar ekranında bir sohbetin ilanına tıklandığında buraya
  // gelinir ve ilgili ilanın detay modalı doğrudan açılır. İlan listede
  // yoksa (silinmiş ilanlar varsayılan listede gelmez) tek tek çekilir.
  const location = useLocation();
  const navigate = useNavigate();
  const acilanDeepLink = useRef(null);

  useEffect(() => {
    const hedefId = new URLSearchParams(location.search).get("ilan");
    if (!hedefId) {
      // Parametre temizlendi; aynı ilana tekrar tıklanabilsin diye kilidi bırak.
      acilanDeepLink.current = null;
      return;
    }
    if (isLoading) return;
    // Parametre temizlenene kadar effect'in iki kez çalışmasını engelle.
    if (acilanDeepLink.current === hedefId) return;
    acilanDeepLink.current = hedefId;

    const id = Number(hedefId);
    const bulunan = ilanlar.find((i) => Number(i.ID) === id);

    const ac = async () => {
      if (bulunan) {
        openDetail(bulunan);
      } else {
        // Silinmiş / filtre dışı ilan: doğrudan ID ile çek
        try {
          const res = await fetch(
            `${BASE_URL}/ILANLAR(${id})?$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress,CEP_NO),KATEGORILER($select=AD,ID)`,
            { headers }
          );
          if (res.ok) {
            openDetail(await res.json());
          } else {
            setError(`İlan #${id} bulunamadı (silinmiş olabilir).`);
          }
        } catch {
          setError(`İlan #${id} yüklenemedi.`);
        }
      }
      // Parametreyi adres çubuğundan temizle: sayfa yenilendiğinde
      // veya geri gelindiğinde modal kendiliğinden tekrar açılmasın.
      navigate("/ilan-yonetimi", { replace: true });
    };

    ac();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isLoading, ilanlar]);

  // ─── MESAJLAŞMA FETCH (Firestore, lazy) ───────────────────────
  // Sohbetler yalnızca "Mesajlaşmalar" sekmesi ilk açıldığında yüklenir.
  const loadSohbetler = useCallback(async (ilanId) => {
    if (!ilanId) return;
    setSohbetLoading(true);
    setSohbetError("");
    try {
      const rows = await getConversationsByAdvert(ilanId);
      setIlanSohbetler(rows);
      setSohbetYuklendi(true);
    } catch (e) {
      setSohbetError(
        "Sohbetler yüklenemedi. Firestore erişimi/izni kontrol edilmeli."
      );
    } finally {
      setSohbetLoading(false);
    }
  }, []);

  // Türkçe-duyarsız arama normalizasyonu (sohbet/mesaj filtreleri için).
  const normTR = (s) =>
    String(s || "").toLocaleLowerCase("tr-TR").replace(/ı/g, "i").replace(/i̇/g, "i");

  const openSohbet = async (sohbet) => {
    setAktifSohbet(sohbet);
    setSohbetMesajlar([]);
    setMesajAra("");
    setMesajLoading(true);
    try {
      const msgs = await getConversationMessages(sohbet.id);
      setSohbetMesajlar(msgs);
    } catch (e) {
      setSohbetError("Mesajlar yüklenemedi.");
    } finally {
      setMesajLoading(false);
    }
  };

  // "Mesajlaşmalar" sekmesine geçilince sohbetleri bir kez yükle.
  useEffect(() => {
    if (detailModal && detailTab === "6" && detailIlan?.ID && !sohbetYuklendi && !sohbetLoading && !sohbetError) {
      loadSohbetler(detailIlan.ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailModal, detailTab, detailIlan]);

  // ─── STATUS CHANGE ────────────────────────────────────────────
  const capitalizeStatus = (s) => {
    if (!s) return s;
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };

  const handleStatusChange = async (id, newStatus) => {
    const eidsIlan = ilanlar.find(i => i.ID === id);
    if (capitalizeStatus(newStatus) === "Aktif" && eidsKapsaminda(eidsIlan)) {
      window.alert(EIDS_UYARI);
      return;
    }
    try {
      const body = { AKTIF_DURUM: capitalizeStatus(newStatus) };
      const ilan = ilanlar.find(i => i.ID === id);
      // OData nullable bool JSON'da 1/0 olarak gelir; loose check kullan.
      if (newStatus === 'Aktif' && ilan?.DEGERLEMEYE_ACIK_MI == true) {
        // Değerleme onaylanınca 60 dk (1 saat) süre ONAYDA başlar (server da aynı değeri authoritative kurar).
        const now = new Date();
        now.setMinutes(now.getMinutes() + 60);
        body.BITIS_TARIHI = now.toISOString();
      }
      await fetch(`${BASE_URL}/ILANLAR(${id})`, {
        method: "PATCH", headers,
        body: JSON.stringify(body),
      });
      setSuccessMsg("Durum güncellendi.");
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch { setError("Durum güncellenemedi."); }
  };

  // ─── VİTRİNE ÇIKAR / KALDIR (hızlı işlem) ────────────────────
  // gun = 7 | 15 | 30 → IsPremium açılır, VITRIN_BITIS_TARIHI = şimdi + gün.
  // gun = null        → vitrinden kaldırılır (IsPremium kapanır, tarih temizlenir).
  const handleVitrin = async (ilan, gun) => {
    try {
      const body = gun === null
        ? { IsPremium: false, VITRIN_BITIS_TARIHI: null }
        : {
            IsPremium: true,
            VITRIN_BITIS_TARIHI: new Date(
              Date.now() + gun * 24 * 60 * 60 * 1000,
            ).toISOString(),
          };
      const res = await fetch(`${BASE_URL}/ILANLAR(${ilan.ID})`, {
        method: "PATCH", headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) { setError("Vitrin durumu güncellenemedi."); return; }

      if (gun === null) {
        setSuccessMsg(`#${ilan.ID} vitrinden kaldırıldı.`);
      } else if ((ilan.AKTIF_DURUM || "").toLowerCase() !== "aktif") {
        // Vitrin sorgusu AKTIF_DURUM eq 'Aktif' şartı da arıyor — durum Aktif
        // değilse ilan premium olsa bile ana sayfada görünmez.
        setSuccessMsg(
          `#${ilan.ID} ${gun} gün vitrine alındı — ancak ilan "${ilan.AKTIF_DURUM}" durumunda, ` +
          `vitrinde görünmesi için Aktif yapılmalı.`,
        );
      } else {
        setSuccessMsg(`#${ilan.ID} ${gun} gün süreyle vitrine çıkarıldı.`);
      }
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch { setError("Vitrin durumu güncellenemedi."); }
  };

  // ─── DELETE (soft) ────────────────────────────────────────────
  // Silme = AKTIF_DURUM 'Silindi'. İlan DB'de kalır, aktif/mobil listede görünmez,
  // "Silinenler" sekmesinde görünür. Geri alınabilir.
  const handleDelete = async (id) => {
    if (!window.confirm("Bu ilanı silmek istediğinize emin misiniz? (Silinenler sekmesinden geri alabilirsiniz.)")) return;
    try {
      await fetch(`${BASE_URL}/ILANLAR(${id})`, {
        method: "PATCH", headers,
        body: JSON.stringify({ AKTIF_DURUM: "Silindi" }),
      });
      setSuccessMsg("İlan silindi (Silinenler sekmesinde).");
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch { setError("Silme işlemi başarısız."); }
  };

  // ─── RESTORE (geri yükle) ─────────────────────────────────────
  const handleRestore = async (id) => {
    if (!window.confirm("Bu ilanı geri yüklemek istiyor musunuz? (Pasif olarak geri gelir.)")) return;
    try {
      await fetch(`${BASE_URL}/ILANLAR(${id})`, {
        method: "PATCH", headers,
        body: JSON.stringify({ AKTIF_DURUM: "Pasif" }),
      });
      setSuccessMsg("İlan geri yüklendi (Pasif).");
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch { setError("Geri yükleme başarısız."); }
  };

  // ─── KALICI SİL (hard delete) ─────────────────────────────────
  // DB'den tamamen siler (geri alınamaz). Sipariş satırlarının ILAN_ID bağı
  // backend'de null'lanır, geçmiş korunur.
  const handlePermanentDelete = async (id) => {
    if (!window.confirm("DİKKAT: Bu ilan kalıcı olarak silinecek ve GERİ ALINAMAZ. Devam edilsin mi?")) return;
    try {
      const res = await fetch(`${BASE_URL}/ILANLAR(${id})`, { method: "DELETE", headers });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Kalıcı silme başarısız.");
        return;
      }
      setSuccessMsg("İlan kalıcı olarak silindi.");
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch { setError("Kalıcı silme başarısız."); }
  };

  // ─── BULK ACTIONS ─────────────────────────────────────────────
  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    if (action === "aktif") {
      const eidsSecili = [...selectedIds].filter(id => eidsKapsaminda(ilanlar.find(i => i.ID === id)));
      if (eidsSecili.length > 0) {
        window.alert(
          `${eidsSecili.length} seçili ilan EİDS kapsamında (taşınmaz / araç / traktör). ` +
          "Bu ilanlar e-Devlet yetki doğrulaması olmadan yayına alınamaz. " +
          "Seçimden çıkarıp tekrar deneyin."
        );
        return;
      }
    }
    const label =
      action === "sil" ? "silmek"
      : action === "gerial" ? "geri yüklemek"
      : action === "kalicisil" ? "KALICI olarak silmek (geri alınamaz)"
      : action === "aktif" ? "aktifleştirmek"
      : "pasifleştirmek";
    if (!window.confirm(`${selectedIds.size} ilanı ${label} istiyor musunuz?`)) return;
    setBulkLoading(true);
    try {
      await Promise.all([...selectedIds].map(id => {
        if (action === "sil") {
          // Soft delete
          return fetch(`${BASE_URL}/ILANLAR(${id})`, {
            method: "PATCH", headers,
            body: JSON.stringify({ AKTIF_DURUM: "Silindi" }),
          });
        }
        if (action === "gerial") {
          return fetch(`${BASE_URL}/ILANLAR(${id})`, {
            method: "PATCH", headers,
            body: JSON.stringify({ AKTIF_DURUM: "Pasif" }),
          });
        }
        if (action === "kalicisil") {
          return fetch(`${BASE_URL}/ILANLAR(${id})`, { method: "DELETE", headers });
        }
        const body = { AKTIF_DURUM: capitalizeStatus(action) };
        if (action === "aktif") {
          const ilan = ilanlar.find(i => i.ID === id);
          // OData nullable bool JSON'da 1/0 olarak gelir; loose check kullan.
          if (ilan?.DEGERLEMEYE_ACIK_MI == true) {
            // Değerleme onaylanınca 60 dk (1 saat) süre ONAYDA başlar (server da authoritative kurar).
            const now = new Date();
            now.setMinutes(now.getMinutes() + 60);
            body.BITIS_TARIHI = now.toISOString();
          }
        }
        return fetch(`${BASE_URL}/ILANLAR(${id})`, {
          method: "PATCH", headers,
          body: JSON.stringify(body),
        });
      }));
      setSuccessMsg(`${selectedIds.size} ilan güncellendi.`);
      setSelectedIds(new Set());
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch { setError("Toplu işlem başarısız."); }
    finally { setBulkLoading(false); }
  };

  // ─── QUICK EDIT SAVE ──────────────────────────────────────────
  const handleEditSave = async () => {
    {
      const onceki = ilanlar.find(i => i.ID === editIlan?.ID);
      const aktifeGecis = editIlan?.AKTIF_DURUM === "Aktif" && onceki?.AKTIF_DURUM !== "Aktif";
      if (aktifeGecis && eidsKapsaminda(editIlan)) {
        window.alert(EIDS_UYARI);
        return;
      }
    }
    setEditLoading(true);
    // Yardımcılar: boş string -> null, sayısal parse (geçersizse null).
    const numOrNull = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = parseFloat(v);
      return Number.isNaN(n) ? null : n;
    };
    const intOrNull = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = parseInt(v, 10);
      return Number.isNaN(n) ? null : n;
    };
    const strOrNull = (v) => {
      const s = (v ?? "").toString().trim();
      return s === "" ? null : s;
    };
    try {
      const body = {
        // ── Temel bilgiler ──
        AD: strOrNull(editIlan.AD),
        ACIKLAMA: strOrNull(editIlan.ACIKLAMA),
        KATEGORI_ID: intOrNull(editIlan.KATEGORI_ID),
        GORSEL_YOLU: strOrNull(editIlan.GORSEL_YOLU),
        POPULERLIK_ETIKETI: strOrNull(editIlan.POPULERLIK_ETIKETI),
        // ── Fiyat / stok ──
        FIYAT: numOrNull(editIlan.FIYAT),
        INDIRIMLI_FIYAT: numOrNull(editIlan.INDIRIMLI_FIYAT),
        PARA_BIRIMI: normalizeCurrency(editIlan.PARA_BIRIMI),
        BIRIM_FIYAT: numOrNull(editIlan.BIRIM_FIYAT),
        URUN_BIRIMI: strOrNull(editIlan.URUN_BIRIMI),
        STOK_ADEDI: intOrNull(editIlan.STOK_ADEDI),
        KARGO_UCRETI: numOrNull(editIlan.KARGO_UCRETI),
        CANLI_KILO: numOrNull(editIlan.CANLI_KILO),
        KARKAS_KILO: numOrNull(editIlan.KARKAS_KILO),
        // ── Konum ──
        KONUM_IL: strOrNull(editIlan.KONUM_IL),
        KONUM_ILCE: strOrNull(editIlan.KONUM_ILCE),
        KONUM_ADRES_DETAY: strOrNull(editIlan.KONUM_ADRES_DETAY),
        // ── Teslimat ──
        TESLIMAT: strOrNull(editIlan.TESLIMAT),
        // ── Durum / tarih ──
        AKTIF_DURUM: editIlan.AKTIF_DURUM,
        BITIS_TARIHI: editIlan.BITIS_TARIHI || null,
        // Premium kapalıyken vitrin süresi anlamsız — artık kalıntı tarih bırakma.
        VITRIN_BITIS_TARIHI: editIlan.IsPremium
          ? editIlan.VITRIN_BITIS_TARIHI || null
          : null,
        // ── Etiketler ──
        IsPremium: !!editIlan.IsPremium,
        IsBold: !!editIlan.IsBold,
        IsColored: !!editIlan.IsColored,
        IsStory: !!editIlan.IsStory,
        SATIS_MI: !!editIlan.SATIS_MI,
        TEKLIFE_ACIK_MI: !!editIlan.TEKLIFE_ACIK_MI,
        DEGERLEMEYE_ACIK_MI: !!editIlan.DEGERLEMEYE_ACIK_MI,
      };
      const res = await fetch(`${BASE_URL}/ILANLAR(${editIlan.ID})`, {
        method: "PATCH", headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data?.message || "Güncelleme başarısız.");
        return;
      }
      setSuccessMsg("İlan güncellendi.");
      setEditModal(false);
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch { setError("Güncelleme başarısız."); }
    finally { setEditLoading(false); }
  };

  // ─── KATEGORİ HİYERARŞİSİ ─────────────────────────────────────
  // KATEGORI_BAGLANTILARI: { UST_KATEGORI_ID, ALT_KATEGORI_ID }.
  const kategoriMap = useMemo(() => {
    const m = {};
    kategoriler.forEach(k => { m[k.ID] = k; });
    return m;
  }, [kategoriler]);

  // Alt kategori ID → üst kategori ID listesi
  const parentsByAlt = useMemo(() => {
    const m = {};
    baglantiler.forEach(b => {
      if (!m[b.ALT_KATEGORI_ID]) m[b.ALT_KATEGORI_ID] = [];
      m[b.ALT_KATEGORI_ID].push(b.UST_KATEGORI_ID);
    });
    return m;
  }, [baglantiler]);

  // EİDS kapsamındaki tüm kategoriler (kökler + torunlar).
  const eidsKategoriSet = useMemo(() => {
    const cocuklar = {};
    baglantiler.forEach(b => {
      (cocuklar[b.UST_KATEGORI_ID] = cocuklar[b.UST_KATEGORI_ID] || []).push(b.ALT_KATEGORI_ID);
    });
    const set = new Set();
    const kuyruk = [...eidsKokler];
    while (kuyruk.length) {
      const id = Number(kuyruk.pop());
      if (set.has(id)) continue;
      set.add(id);
      (cocuklar[id] || []).forEach(c => kuyruk.push(c));
    }
    return set;
  }, [baglantiler, eidsKokler]);

  // Değerleme satış ilanı değil; EİDS kapsamı dışında.
  const eidsKapsaminda = useCallback(
    (ilan) => !!ilan && !(ilan.DEGERLEMEYE_ACIK_MI == true) && eidsKategoriSet.has(Number(ilan.KATEGORI_ID)),
    [eidsKategoriSet],
  );

  const EIDS_UYARI =
    "EİDS geçiş dönemi: Bu ilan taşınmaz / araç / traktör kategorisinde. Ticaret Bakanlığı " +
    "mevzuatı gereği e-Devlet (EİDS) yetki doğrulaması olmadan yayına alınamaz; " +
    "doğrulamasız ilan yayımlayan platformlara idari para cezası uygulanıyor. " +
    "Entegrasyon tamamlanana kadar ONAYLAMAYIN.";

  // Bir kategorinin kök→yaprak yolunu ["Ana", "Alt", ...] olarak döndürür.
  const kategoriYolu = useCallback((katId) => {
    const path = [];
    let cur = Number(katId);
    const guard = new Set(); // döngü koruması
    while (cur && kategoriMap[cur] && !guard.has(cur)) {
      guard.add(cur);
      path.unshift(kategoriMap[cur].AD);
      const parents = parentsByAlt[cur];
      cur = parents && parents.length ? Number(parents[0]) : null;
    }
    return path;
  }, [kategoriMap, parentsByAlt]);

  // Kategori seçim listesi: her kategori için tam yol metni ve seviye bilgisi.
  const kategoriSecenekleri = useMemo(() => {
    return kategoriler
      .map(k => {
        const path = kategoriYolu(k.ID);
        return {
          id: k.ID,
          ad: k.AD,
          yol: path,                    // ["Ana", "Alt", "AltAlt"]
          yolText: path.join(" › "),    // "Ana › Alt › AltAlt"
          seviye: Math.max(0, path.length - 1),
          altMi: (parentsByAlt[k.ID]?.length || 0) > 0,
        };
      })
      .sort((a, b) => a.yolText.localeCompare(b.yolText, "tr"));
  }, [kategoriler, kategoriYolu, parentsByAlt]);

  // Düzenleme modalı için arama filtresi (Türkçe-duyarsız, yol üzerinde arar).
  const filtrelenmisKategoriler = useMemo(() => {
    if (!editKatSearch.trim()) return kategoriSecenekleri;
    // "traktor" → "Traktör", "john deere 5050" gibi çok kelimeli aramalar da eşleşir.
    return kategoriSecenekleri.filter(k =>
      matchesSearch(k, editKatSearch, [k.yolText, k.id])
    );
  }, [kategoriSecenekleri, editKatSearch]);

  // ─── COMPUTED ─────────────────────────────────────────────────
  // Alt kategorisi olan kategoriler: ilan buradaysa "boşta kalmış" sayılır.
  const ustKategoriIdleri = useMemo(
    () => new Set(baglantiler.map(b => Number(b.UST_KATEGORI_ID))),
    [baglantiler]
  );
  const isBosta = useCallback(
    (i) => ustKategoriIdleri.has(Number(i.KATEGORI_ID)),
    [ustKategoriIdleri]
  );
  const isSilindi = (i) => (i.AKTIF_DURUM || "").toLowerCase() === "silindi";

  const stats = useMemo(() => {
    // Silinen ilanlar canlı sayımlardan hariç tutulur.
    const canli = ilanlar.filter(i => !isSilindi(i));
    const aktif = canli.filter(i => (i.AKTIF_DURUM || "").toLowerCase() === "aktif").length;
    const pasif = canli.filter(i => (i.AKTIF_DURUM || "").toLowerCase() === "pasif").length;
    const premium = canli.filter(i => i.IsPremium).length;
    const totalTeklif = Object.values(teklifSayilari).reduce((a, b) => a + b, 0);
    const degerleme = canli.filter(i => i.DEGERLEMEYE_ACIK_MI).length;
    // Onay bekleyen değerlemeler: değerleme ilanı + AKTIF_DURUM='Beklemede'
    const bekleyenDegerleme = canli.filter(
      i => i.DEGERLEMEYE_ACIK_MI && (i.AKTIF_DURUM || "").toLowerCase() === "beklemede"
    ).length;
    const duzenlendi = canli.filter(isDuzenlendi).length;
    const silinen = ilanlar.filter(isSilindi).length;
    const bosta = bostaIlanlar.filter(i => !isSilindi(i) && isBosta(i)).length;
    return { total: canli.length, aktif, pasif, premium, totalTeklif, degerleme, bekleyenDegerleme, duzenlendi, silinen, bosta };
  }, [ilanlar, teklifSayilari, bostaIlanlar, isBosta]);

  // Hızlı aralık seçimi manuel tarihleri ezerek etkin başlangıç/bitiş üretir.
  const effectiveRange = useMemo(() => {
    const now = new Date();
    const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
    const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
    if (datePreset === "today") return { start: startOfDay(now), end: endOfDay(now) };
    if (datePreset === "7days") { const s = new Date(now); s.setDate(s.getDate() - 6); return { start: startOfDay(s), end: endOfDay(now) }; }
    if (datePreset === "30days") { const s = new Date(now); s.setDate(s.getDate() - 29); return { start: startOfDay(s), end: endOfDay(now) }; }
    if (datePreset === "thismonth") return { start: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), end: endOfDay(now) };
    return {
      start: dateStart ? startOfDay(new Date(dateStart)) : null,
      end: dateEnd ? endOfDay(new Date(dateEnd)) : null,
    };
  }, [datePreset, dateStart, dateEnd]);

  const filteredIlanlar = useMemo(() => {
    // "Alt Kategori Bekleyen" görünümü son 1000 ilanla sınırlı değil; ayrı kaynaktan.
    let kaynak = filterTip === "bosta" ? bostaIlanlar : ilanlar;
    if (aramaSonuclari.length) {
      const varOlan = new Set(kaynak.map(i => i.ID));
      kaynak = [...kaynak, ...aramaSonuclari.filter(i => !varOlan.has(i.ID))];
    }
    const list = kaynak.filter(i => {
      // Silinenler görünümü: yalnızca 'Silindi'. Normal görünüm: 'Silindi' hariç.
      if (showDeleted ? !isSilindi(i) : isSilindi(i)) return false;
      if (filterDurum !== "all" && (i.AKTIF_DURUM || "").toLowerCase() !== filterDurum.toLowerCase()) return false;
      if (filterKategori !== "all" && String(i.KATEGORI_ID) !== filterKategori) return false;
      if (filterTip === "satis" && !i.SATIS_MI) return false;
      if (filterTip === "teklif" && !i.TEKLIFE_ACIK_MI) return false;
      if (filterTip === "premium" && !i.IsPremium) return false;
      if (filterTip === "degerleme" && !i.DEGERLEMEYE_ACIK_MI) return false;
      if (filterTip === "duzenlendi" && !isDuzenlendi(i)) return false;
      if (filterTip === "bosta" && !isBosta(i)) return false;
      // Oluşturma tarihi aralığı
      if (effectiveRange.start || effectiveRange.end) {
        if (!i.OLUSTURMA_TARIHI) return false;
        const d = parseApiDate(i.OLUSTURMA_TARIHI);
        if (!d) return false;
        if (effectiveRange.start && d < effectiveRange.start) return false;
        if (effectiveRange.end && d > effectiveRange.end) return false;
      }
      if (searchQuery.trim()) {
        // Başlık, ID, ilan sahibi, kategori yolu ve konumda Türkçe duyarsız arama.
        return matchesSearch(i, searchQuery, [
          i.AD,
          i.ID,
          i.KULLANICILAR?.AD_SOYAD,
          i.KULLANICILAR?.Mail_Adress,
          i.KULLANICILAR?.CEP_NO,
          kategoriYolu(i.KATEGORI_ID).join(" "),
          i.KONUM_IL,
          i.KONUM_ILCE,
        ]);
      }
      return true;
    });
    // Varsayılan: en son açılan ilan en üstte. Eşitlikte de yeni ilan önde kalır.
    const { alan, yon } = siralama;
    const carpan = yon === "asc" ? 1 : -1;
    const tarih = (i) => parseApiDate(i.OLUSTURMA_TARIHI)?.getTime() ?? 0;
    return [...list].sort((a, b) => {
      const av = siralamaDegeri(a, alan, teklifSayilari);
      const bv = siralamaDegeri(b, alan, teklifSayilari);
      if (av === null && bv !== null) return 1;
      if (bv === null && av !== null) return -1;
      if (av !== null && bv !== null && av !== bv) {
        const fark = typeof av === "string" ? av.localeCompare(bv, "tr") : av - bv;
        if (fark !== 0) return fark * carpan;
      }
      return tarih(b) - tarih(a);
    });
  }, [ilanlar, bostaIlanlar, aramaSonuclari, isBosta, filterDurum, filterKategori, filterTip, searchQuery, showDeleted, effectiveRange, kategoriYolu, siralama, teklifSayilari]);

  const statusBadge = (s) => {
    const lower = typeof s === "string" ? s.toLowerCase() : "";
    if (lower === "aktif") return <Badge color="success">Aktif</Badge>;
    if (lower === "pasif") return <Badge color="danger">Pasif</Badge>;
    if (lower === "beklemede") return <Badge color="warning">Beklemede</Badge>;
    if (lower === "silindi") return <Badge color="dark">Silindi</Badge>;
    return <Badge color="secondary">{s || "?"}</Badge>;
  };

  // ─── TOGGLE SELECT ────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredIlanlar.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredIlanlar.map(i => i.ID)));
    }
  };

  // ─── TABLE COLUMNS ────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      id: "select",
      header: () => (
        <Input
          type="checkbox"
          checked={selectedIds.size > 0 && selectedIds.size === filteredIlanlar.length}
          onChange={toggleSelectAll}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => (
        <Input
          type="checkbox"
          checked={selectedIds.has(cell.row.original.ID)}
          onChange={() => toggleSelect(cell.row.original.ID)}
          onClick={e => e.stopPropagation()}
        />
      ),
    },
    {
      header: "Görsel",
      accessorKey: "GORSEL_YOLU",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const url = cell.getValue();
        return url ? (
          <img
            src={url}
            alt="ilan"
            style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6 }}
          />
        ) : (
          <div
            className="bg-light border rounded d-flex align-items-center justify-content-center text-muted"
            style={{ width: 44, height: 44, borderRadius: 6 }}
          >
            <i className="bx bx-image font-size-18" />
          </div>
        );
      },
    },
    {
      // Başlıktaki ayrı "Arama..." kutusu kaldırıldı: üstteki arama zaten başlık,
      // ID, satıcı ve kategori yolunda arıyor.
      header: () => <SiraliBaslik etiket="İlan" alan="ad" ilkYon="asc" siralama={siralama} setSiralama={setSiralama} />,
      id: "AD",
      accessorKey: "AD",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const i = cell.row.original;
        return (
          <div style={{ maxWidth: 240 }}>
            <div className="fw-semibold text-truncate">{i.AD}</div>
            {ilanWebLinki(i) && (
              <div className="d-flex align-items-center gap-2 font-size-11">
                <span className="text-muted">#{i.ID}</span>
                <a
                  href={ilanWebLinki(i)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  title={
                    i.AKTIF_DURUM === "Aktif"
                      ? ilanWebLinki(i)
                      : `${ilanWebLinki(i)}\nİlan aktif değil; sitede görünmeyebilir.`
                  }
                >
                  <i className="bx bx-link-external me-1" />Sitede aç
                </a>
                <a
                  href="#!"
                  className="text-muted"
                  onClick={async e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (await linkiKopyala(ilanWebLinki(i))) {
                      setSuccessMsg(`#${i.ID} linki kopyalandı.`);
                      setTimeout(() => setSuccessMsg(""), 2000);
                    }
                  }}
                  title="Linki kopyala"
                >
                  <i className="bx bx-copy" />
                </a>
              </div>
            )}
            <div className="d-flex flex-wrap gap-1 mt-1">
              {i.KATEGORILER?.AD && (
                // Tam yol: yalnız yaprak ad ("Diğer") yazınca hangi dalda olduğu
                // anlaşılmıyordu. Uzun yolda baş kısım kısaltılır, tamamı title'da.
                // Mobil görünümde tek satırda kesiliyordu: badge'in nowrap'ı
                // kaldırılıp en fazla 3 satıra sarılır; sığmazsa 3. satır "…" ile biter.
                <span
                  className="badge bg-light text-dark border font-size-10 text-start"
                  style={{
                    maxWidth: "100%",
                    whiteSpace: "normal",
                    lineHeight: 1.35,
                    wordBreak: "break-word",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                  title={kategoriYolu(i.KATEGORI_ID).join(" › ") || i.KATEGORILER.AD}
                >
                  {kategoriYolu(i.KATEGORI_ID).join(" › ") || i.KATEGORILER.AD}
                </span>
              )}
              {eidsKapsaminda(i) && (
                <span
                  className="badge bg-danger font-size-10"
                  title="EİDS geçiş dönemi: e-Devlet yetki doğrulaması olmadan yayına alınamaz — onaylamayın."
                >
                  <i className="bx bx-error me-1" />EİDS — onaylamayın
                </span>
              )}
              {i.KONUM_IL && (
                <span className="badge bg-light text-muted border font-size-10">
                  <i className="bx bx-map-pin me-1" />{i.KONUM_IL}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      header: "Satıcı",
      accessorKey: "KULLANICILAR",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const u = cell.getValue();
        return u ? (
          <div>
            <div className="fw-medium font-size-13">{u.AD_SOYAD}</div>
            <small className="text-muted">{u.Mail_Adress}</small>
          </div>
        ) : <span className="text-muted">-</span>;
      },
    },
    {
      header: () => <SiraliBaslik etiket="Fiyat" alan="fiyat" siralama={siralama} setSiralama={setSiralama} />,
      id: "FIYAT",
      accessorKey: "FIYAT",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const fiyat = cell.getValue();
        const row = cell.row.original;
        const indirimli = row.INDIRIMLI_FIYAT;
        const birim = row.URUN_BIRIMI;
        const cur = row.PARA_BIRIMI;
        return (
          <div>
            {indirimli ? (
              <>
                <div className="text-decoration-line-through text-muted font-size-11">
                  {formatIlanPrice(fiyat, cur)}
                </div>
                <div className="fw-bold text-success">
                  {formatIlanPrice(indirimli, cur)}
                </div>
              </>
            ) : (
              <div className="fw-medium">
                {fiyat ? formatIlanPrice(fiyat, cur) : "-"}
              </div>
            )}
            {birim && <small className="text-muted">/ {birim}</small>}
            {normalizeCurrency(cur) === "EUR" && (
              <div><Badge color="info" className="font-size-10">EUR</Badge></div>
            )}
          </div>
        );
      },
    },
    {
      id: "STOK",
      header: () => <SiraliBaslik etiket="Stok" alan="stok" siralama={siralama} setSiralama={setSiralama} />,
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const stok = cell.row.original.STOK_ADEDI;
        if (stok === null || stok === undefined) {
          return <span className="text-muted font-size-12" title="Stok girilmemiş (sınırsız)">∞</span>;
        }
        if (Number(stok) <= 0) return <Badge color="danger" className="font-size-11">Tükendi</Badge>;
        return (
          <span className={`fw-semibold font-size-13 ${Number(stok) <= 5 ? "text-warning" : ""}`} title={Number(stok) <= 5 ? "Az stok" : undefined}>
            <i className="bx bx-package me-1 text-muted" />{Number(stok).toLocaleString("tr-TR")}
          </span>
        );
      },
    },
    {
      id: "GORUNTULENME",
      header: () => <SiraliBaslik etiket="Görüntülenme" alan="goruntulenme" siralama={siralama} setSiralama={setSiralama} />,
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const g = Number(cell.row.original.GÖRÜNTÜLENME_SAYISI ?? 0);
        return (
          <span className={`font-size-13 ${g > 0 ? "fw-semibold" : "text-muted"}`}>
            <i className="bx bx-show me-1 text-muted" />{g.toLocaleString("tr-TR")}
          </span>
        );
      },
    },
    {
      id: "TEKLIF",
      header: () => <SiraliBaslik etiket="Teklifler" alan="teklif" siralama={siralama} setSiralama={setSiralama} />,
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const count = teklifSayilari[cell.row.original.ID] || 0;
        return (
          <span className={`badge font-size-12 ${count > 0 ? "bg-primary" : "bg-light text-dark"}`}>
            {count}
          </span>
        );
      },
    },
    {
      header: "Durum",
      accessorKey: "AKTIF_DURUM",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const i = cell.row.original;
        return (
          <div>
            {statusBadge(cell.getValue())}
            <div className="d-flex flex-wrap gap-1 mt-1">
              {isDuzenlendi(i) && (
                <Badge color="danger" className="font-size-10">
                  <i className="bx bx-edit-alt me-1" />Düzenlendi
                </Badge>
              )}
              {i.IsPremium && (
                <Badge color="warning" className="font-size-10" title={vitrinDurumu(i).baslik}>
                  Premium{vitrinDurumu(i).etiket}
                </Badge>
              )}
              {i.IsBold && <Badge color="dark" className="font-size-10">Kalın</Badge>}
              {i.SATIS_MI && <Badge color="info" className="font-size-10">Satış</Badge>}
              {i.TEKLIFE_ACIK_MI && <Badge color="success" className="font-size-10">Teklif</Badge>}
              {i.DEGERLEMEYE_ACIK_MI && <Badge color="secondary" className="font-size-10">Değerleme</Badge>}
              {i.IsStory && <Badge color="purple" style={{ background: "#7367f0" }} className="font-size-10">Story</Badge>}
            </div>
          </div>
        );
      },
    },
    {
      header: () => <SiraliBaslik etiket="Tarih" alan="tarih" siralama={siralama} setSiralama={setSiralama} />,
      id: "OLUSTURMA_TARIHI",
      accessorKey: "OLUSTURMA_TARIHI",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        const bitis = cell.row.original.BITIS_TARIHI;
        const fmt = (v) => formatApiDateShort(v);
        return (
          <div className="font-size-12">
            <div>{d ? fmt(d) : "-"}</div>
            {bitis && (
              <div className="text-danger">
                <i className="bx bx-time me-1" />
                {fmt(bitis)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const i = cell.row.original;
        const isAktif = (i.AKTIF_DURUM || "").toLowerCase() === "aktif";
        // Silinenler görünümünde: Detay + Geri Yükle + Kalıcı Sil.
        if (showDeleted) {
          return (
            <div className="d-flex gap-1">
              <Button color="info" size="sm" outline title="Detay" onClick={() => openDetail(i)}>
                <i className="bx bx-show" />
              </Button>
              <Button color="success" size="sm" outline title="Geri Yükle" onClick={() => handleRestore(i.ID)}>
                <i className="bx bx-undo" />
              </Button>
              <Button color="danger" size="sm" title="Kalıcı Sil" onClick={() => handlePermanentDelete(i.ID)}>
                <i className="bx bx-trash" />
              </Button>
            </div>
          );
        }
        return (
          <div className="d-flex gap-1">
            <Button color="info" size="sm" outline title="Detay" onClick={() => openDetail(i)}>
              <i className="bx bx-show" />
            </Button>
            <Button color="secondary" size="sm" outline title="Sahibine bildirim / ara" onClick={() => setIletisimIlan(i)}>
              <i className="bx bx-message-rounded-dots" />
            </Button>
            <Button
              color="primary" size="sm" outline title="Düzenle"
              onClick={() => { setEditIlan({ ...i }); setEditModal(true); }}
            >
              <i className="bx bx-pencil" />
            </Button>
            <Button
              color={isAktif ? "warning" : "success"} size="sm" outline
              title={isAktif ? "Pasifleştir" : "Aktifleştir"}
              onClick={() => handleStatusChange(i.ID, isAktif ? "Pasif" : "Aktif")}
            >
              <i className={`bx ${isAktif ? "bx-pause" : "bx-play"}`} />
            </Button>
            {/* Vitrin: tek tıkla aç/kapa. Dropdown kullanılmıyor — tablo
                "table-responsive" (overflow-x:auto) içinde olduğu için açılan menü
                kırpılıp görünmüyordu. Farklı süre gerekiyorsa Düzenle modalında
                7/15/30 gün butonları var. */}
            <Button
              color="warning" size="sm"
              outline={!vitrindeMi(i)}
              title={
                vitrindeMi(i)
                  ? `${vitrinDurumu(i).baslik} — kaldırmak için tıklayın`
                  : "7 gün süreyle vitrine çıkar (farklı süre için Düzenle)"
              }
              onClick={() => handleVitrin(i, vitrindeMi(i) ? null : 7)}
            >
              <i className="bx bxs-star" />
            </Button>
            <Button color="danger" size="sm" outline title="Sil" onClick={() => handleDelete(i.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [teklifSayilari, selectedIds, filteredIlanlar, showDeleted, kategoriYolu, eidsKapsaminda, siralama]);

  // ─── RENDER ───────────────────────────────────────────────────
  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Yönetim" breadcrumbItem="İlan Yönetimi" />

          {successMsg && (
            <Alert color="success" className="border-0" toggle={() => setSuccessMsg("")}>
              <i className="mdi mdi-check-circle me-2" />{successMsg}
            </Alert>
          )}
          {error && (
            <Alert color="danger" className="border-0" toggle={() => setError(null)}>
              <i className="mdi mdi-alert-outline me-2" />{error}
            </Alert>
          )}

          {/* ── İSTATİSTİK KARTLARI ───────────────────────────── */}
          <Row className="mb-4">
            {[
              {
                label: "Toplam İlan", value: stats.total, icon: "bx-store-alt", color: "primary",
                active: !showDeleted && filterDurum === "all" && filterTip === "all",
                action: () => { setShowDeleted(false); setFilterDurum("all"); setFilterTip("all"); },
              },
              {
                label: "Aktif", value: stats.aktif, icon: "bx-check-circle", color: "success",
                active: !showDeleted && filterDurum === "Aktif",
                action: () => { setShowDeleted(false); setFilterTip("all"); setFilterDurum("Aktif"); },
              },
              {
                label: "Pasif", value: stats.pasif, icon: "bx-pause-circle", color: "danger",
                active: !showDeleted && filterDurum === "Pasif",
                action: () => { setShowDeleted(false); setFilterTip("all"); setFilterDurum("Pasif"); },
              },
              {
                label: "Premium", value: stats.premium, icon: "bxs-star", color: "warning",
                active: !showDeleted && filterTip === "premium",
                action: () => { setShowDeleted(false); setFilterDurum("all"); setFilterTip("premium"); },
              },
              {
                label: "Değerleme", value: stats.degerleme, icon: "bx-bar-chart-alt-2", color: "secondary",
                active: !showDeleted && filterTip === "degerleme" && filterDurum === "all",
                action: () => { setShowDeleted(false); setFilterDurum("all"); setFilterTip("degerleme"); },
              },
              {
                label: "Onay Bekleyen Değerleme",
                value: stats.bekleyenDegerleme,
                icon: "bx-time-five",
                color: stats.bekleyenDegerleme > 0 ? "warning" : "secondary",
                highlight: stats.bekleyenDegerleme > 0,
                active: !showDeleted && filterTip === "degerleme" && filterDurum === "Beklemede",
                action: () => { setShowDeleted(false); setFilterTip("degerleme"); setFilterDurum("Beklemede"); },
              },
              {
                label: "Toplam Teklif", value: stats.totalTeklif, icon: "bx-message-square-dots", color: "info",
                active: !showDeleted && filterTip === "teklif",
                action: () => { setShowDeleted(false); setFilterDurum("all"); setFilterTip("teklif"); },
              },
              {
                label: "Düzenlenen", value: stats.duzenlendi, icon: "bx-edit-alt", color: "danger",
                active: !showDeleted && filterTip === "duzenlendi",
                action: () => { setShowDeleted(false); setFilterDurum("all"); setFilterTip("duzenlendi"); },
              },
              {
                label: "Alt Kategori Bekleyen",
                value: stats.bosta,
                icon: "bx-git-branch",
                color: stats.bosta > 0 ? "warning" : "secondary",
                highlight: stats.bosta > 0,
                active: !showDeleted && filterTip === "bosta",
                action: () => { setShowDeleted(false); setFilterDurum("all"); setFilterTip("bosta"); },
              },
              {
                label: "Silinen", value: stats.silinen, icon: "bx-trash", color: "dark",
                active: showDeleted,
                action: () => { setShowDeleted(true); setFilterDurum("all"); setFilterTip("all"); },
              },
            ].map((s, i) => (
              <Col key={i} xs={6} sm={4} lg={3} xxl={2}>
                <Card
                  className={`mini-stats-wid mb-3${s.active ? ` border border-2 border-${s.color} shadow-sm` : s.highlight ? " border border-warning" : ""}`}
                  style={{ cursor: "pointer", transition: "transform .1s" }}
                  onClick={s.action}
                  title={`${s.label} filtrele`}
                >
                  <CardBody className="py-3 px-3">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        <p className="text-muted fw-medium mb-1 font-size-13 text-truncate">{s.label}</p>
                        <h4 className="mb-0">
                          {isLoading ? <Spinner size="sm" color={s.color} /> : s.value}
                        </h4>
                      </div>
                      <div className={`avatar-sm rounded-circle bg-${s.color} align-self-center mini-stat-icon ms-2`}>
                        <span className={`avatar-title rounded-circle bg-${s.color}`}>
                          <i className={`bx ${s.icon} font-size-20`} />
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {filterTip === "bosta" && !showDeleted && (
            <Alert color="warning" className="border-0">
              <i className="bx bx-git-branch me-2" />
              Bu ilanlar <strong>alt kategorisi olan</strong> bir kategoride duruyor (ör. traktörde
              model yerine markada). Kullanıcı kategori sayfasında modele indiğinde bu ilanları
              göremez. <strong>Düzenle</strong> ile doğru alt kategoriyi seçin; kaydedince listeden düşer.
              Yalnızca traktörler için aramaya <em>traktör</em> yazın.
            </Alert>
          )}

          {/* ── ANA TABLO ─────────────────────────────────────── */}
          <Card className="shadow-sm">
            <CardBody>
              {/* Toolbar */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                  <h4 className="card-title mb-1">
                    {showDeleted ? "Silinen İlanlar" : "Tüm İlanlar"}
                  </h4>
                  <p className="text-muted mb-0 font-size-13">
                    {filteredIlanlar.length} ilan listeleniyor
                    {selectedIds.size > 0 && (
                      <span className="text-primary ms-2">({selectedIds.size} seçili)</span>
                    )}
                  </p>
                </div>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  {selectedIds.size > 0 && (
                    <div className="d-flex gap-1">
                      {showDeleted ? (
                        <>
                          <Button
                            color="success" size="sm"
                            disabled={bulkLoading}
                            onClick={() => handleBulkAction("gerial")}
                          >
                            {bulkLoading ? <Spinner size="sm" /> : "Geri Yükle"}
                          </Button>
                          <Button
                            color="danger" size="sm"
                            disabled={bulkLoading}
                            onClick={() => handleBulkAction("kalicisil")}
                          >
                            Kalıcı Sil
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            color="success" size="sm"
                            disabled={bulkLoading}
                            onClick={() => handleBulkAction("aktif")}
                          >
                            {bulkLoading ? <Spinner size="sm" /> : "Aktifleştir"}
                          </Button>
                          <Button
                            color="warning" size="sm"
                            disabled={bulkLoading}
                            onClick={() => handleBulkAction("pasif")}
                          >
                            Pasifleştir
                          </Button>
                          <Button
                            color="danger" size="sm"
                            disabled={bulkLoading}
                            onClick={() => handleBulkAction("sil")}
                          >
                            Sil
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                  <Button
                    color={showDeleted ? "primary" : "dark"}
                    size="sm" outline={!showDeleted}
                    onClick={() => {
                      setShowDeleted(v => !v);
                      setSelectedIds(new Set());
                      setFilterDurum("all");
                      setFilterTip("all");
                    }}
                  >
                    <i className={`bx ${showDeleted ? "bx-arrow-back" : "bx-trash"} me-1`} />
                    {showDeleted ? "Aktif Listeye Dön" : `Silinenler (${stats.silinen})`}
                  </Button>
                  <Button color="light" size="sm" className="border" onClick={fetchAll} disabled={isLoading}>
                    <i className="bx bx-refresh me-1" />Yenile
                  </Button>
                </div>
              </div>

              {/* Filtreler */}
              <Row className="g-2 mb-3">
                <Col md={3}>
                  <Input
                    type="search"
                    bsSize="sm"
                    placeholder="İlan adı, satıcı, ID ara..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                  {aramaYukleniyor && (
                    <small className="text-muted"><Spinner size="sm" className="me-1" />Tüm ilanlarda aranıyor…</small>
                  )}
                </Col>
                <Col md={2}>
                  <Input
                    type="select" bsSize="sm"
                    value={filterDurum}
                    onChange={e => setFilterDurum(e.target.value)}
                  >
                    <option value="all">Tüm Durumlar</option>
                    <option value="Aktif">Aktif</option>
                    <option value="Pasif">Pasif</option>
                    <option value="Beklemede">Beklemede</option>
                  </Input>
                </Col>
                <Col md={3}>
                  <Input
                    type="select" bsSize="sm"
                    value={filterKategori}
                    onChange={e => setFilterKategori(e.target.value)}
                  >
                    <option value="all">Tüm Kategoriler</option>
                    {kategoriler.map(k => (
                      <option key={k.ID} value={k.ID}>{k.AD}</option>
                    ))}
                  </Input>
                </Col>
                <Col md={2}>
                  <Input
                    type="select" bsSize="sm"
                    value={filterTip}
                    onChange={e => setFilterTip(e.target.value)}
                  >
                    <option value="all">Tüm Tipler</option>
                    <option value="satis">Satış</option>
                    <option value="teklif">Teklif Açık</option>
                    <option value="premium">Premium</option>
                    <option value="degerleme">Değerleme</option>
                    <option value="duzenlendi">Düzenlenenler</option>
                  </Input>
                </Col>
                <Col md={2}>
                  <Input
                    type="select" bsSize="sm"
                    value={datePreset}
                    onChange={e => { setDatePreset(e.target.value); if (e.target.value !== "all") { setDateStart(""); setDateEnd(""); } }}
                    title="Oluşturma tarihi hızlı aralık"
                  >
                    <option value="all">Tüm Tarihler</option>
                    <option value="today">Bugün</option>
                    <option value="7days">Son 7 Gün</option>
                    <option value="30days">Son 30 Gün</option>
                    <option value="thismonth">Bu Ay</option>
                  </Input>
                </Col>
              </Row>
              <Row className="g-2 mb-3 align-items-end">
                <Col md={3}>
                  <Label className="form-label mb-1 font-size-11 text-muted">Oluşturma Tarihi Aralığı</Label>
                  <div className="d-flex gap-1">
                    <Input
                      type="date" bsSize="sm" value={dateStart}
                      onChange={e => { setDateStart(e.target.value); setDatePreset("all"); }}
                      title="Başlangıç"
                    />
                    <Input
                      type="date" bsSize="sm" value={dateEnd}
                      onChange={e => { setDateEnd(e.target.value); setDatePreset("all"); }}
                      title="Bitiş"
                    />
                  </div>
                </Col>
                <Col md={3}>
                  <Label className="form-label mb-1 font-size-11 text-muted">Sıralama</Label>
                  <Input
                    type="select" bsSize="sm"
                    value={`${siralama.alan}:${siralama.yon}`}
                    onChange={e => {
                      const [alan, yon] = e.target.value.split(":");
                      setSiralama({ alan, yon });
                    }}
                  >
                    {!SIRALAMA_SECENEKLERI.some(s => s.deger === `${siralama.alan}:${siralama.yon}`) && (
                      <option value={`${siralama.alan}:${siralama.yon}`}>Özel sıralama</option>
                    )}
                    {SIRALAMA_SECENEKLERI.map(s => (
                      <option key={s.deger} value={s.deger}>{s.etiket}</option>
                    ))}
                  </Input>
                </Col>
                <Col md={2}>
                  <Button
                    color="light" size="sm" className="border w-100"
                    onClick={() => {
                      setSiralama(VARSAYILAN_SIRALAMA);
                      setFilterDurum("all");
                      setFilterKategori("all");
                      setFilterTip("all");
                      setSearchQuery("");
                      setDateStart("");
                      setDateEnd("");
                      setDatePreset("all");
                    }}
                  >
                    <i className="bx bx-x me-1" />Sıfırla
                  </Button>
                </Col>
              </Row>

              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Yükleniyor...</p>
                </div>
              ) : (
                <TableContainer
                  columns={columns}
                  data={filteredIlanlar}
                  resetPageKey={`${siralama.alan}:${siralama.yon}`}
                  isGlobalFilter={false}
                  isPagination={true}
                  isCustomPageSize={true}
                  tableClass="table-hover table-bordered align-middle nowrap w-100 mb-0"
                  theadClass="table-light"
                  paginationWrapper="d-flex justify-content-end mt-3 pagination-rounded"
                />
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* ══ DETAY MODAL ══════════════════════════════════════════ */}
      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="xl" scrollable>
        <ModalHeader toggle={() => setDetailModal(false)} className="bg-light">
          <span className="fw-bold text-primary">#{detailIlan?.ID}</span>
          {" — "}{detailIlan?.AD}
          {ilanWebLinki(detailIlan) && (
            <span className="ms-3 d-inline-flex gap-2 align-middle">
              <a
                className="btn btn-sm btn-outline-primary py-0"
                href={ilanWebLinki(detailIlan)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bx bx-link-external me-1" />Sitede aç
              </a>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary py-0"
                onClick={async () => {
                  if (await linkiKopyala(ilanWebLinki(detailIlan))) {
                    setSuccessMsg(`#${detailIlan.ID} linki kopyalandı.`);
                    setTimeout(() => setSuccessMsg(""), 2000);
                  }
                }}
              >
                <i className="bx bx-copy me-1" />Linki kopyala
              </button>
            </span>
          )}
        </ModalHeader>
        <ModalBody className="p-0">
          {detailLoading ? (
            <div className="text-center py-5"><Spinner color="primary" /></div>
          ) : detailIlan && (
            <>
              <Nav tabs className="nav-tabs-custom px-3 pt-2 bg-white border-bottom">
                {[
                  { id: "1", icon: "bx-info-circle", label: "Genel" },
                  { id: "2", icon: "bx-images", label: `Medya (${ilanMedya.length})` },
                  { id: "3", icon: "bx-message-square-dots", label: `Teklifler (${ilanTeklifler.length})` },
                  { id: "4", icon: "bx-bar-chart-alt-2", label: `Değerlemeler (${ilanDegerlemeler.length})` },
                  { id: "5", icon: "bx-error-circle", label: `Şikayetler (${ilanSikayetler.length})` },
                  { id: "6", icon: "bx-chat", label: "Mesajlaşmalar" },
                ].map(tab => (
                  <NavItem key={tab.id}>
                    <NavLink
                      className={classnames({ active: detailTab === tab.id })}
                      onClick={() => setDetailTab(tab.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <i className={`bx ${tab.icon} me-1`} />{tab.label}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent activeTab={detailTab} className="p-3">
                {/* Tab 1: Genel */}
                <TabPane tabId="1">
                  <Row>
                    <Col md={4}>
                      {(() => {
                        // Önizlemede tüm fotoğraflar: büyük görsel + küçük resim
                        // şeridi (eskiden yalnız kapak görünüyordu).
                        const fotolar = ilanMedya
                          .filter(m => String(m.MEDYA_TIP || "").toLowerCase() !== "video" && (m.MEDYA_URL || m.MEDYA_YOLU))
                          .map(m => m.MEDYA_URL || m.MEDYA_YOLU);
                        if (fotolar.length === 0 && detailIlan.GORSEL_YOLU) fotolar.push(detailIlan.GORSEL_YOLU);
                        if (fotolar.length === 0) return null;
                        const secili = fotolar[Math.min(onizlemeIdx, fotolar.length - 1)];
                        return (
                          <div className="mb-3">
                            <a href={secili} target="_blank" rel="noreferrer">
                              <img
                                src={secili}
                                alt={detailIlan.AD}
                                className="img-fluid rounded shadow-sm w-100"
                                style={{ maxHeight: 260, objectFit: "cover" }}
                              />
                            </a>
                            {fotolar.length > 1 && (
                              <div className="d-flex flex-wrap gap-1 mt-2">
                                {fotolar.map((u, idx) => (
                                  <img
                                    key={idx}
                                    src={u}
                                    alt={`Foto ${idx + 1}`}
                                    onClick={() => setOnizlemeIdx(idx)}
                                    className="rounded"
                                    style={{
                                      width: 52, height: 52, objectFit: "cover", cursor: "pointer",
                                      border: secili === u ? "2px solid #556ee6" : "1px solid #dee2e6",
                                    }}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {ilanMedya.some(m => String(m.MEDYA_TIP || "").toLowerCase() !== "video" && (m.MEDYA_URL || m.MEDYA_YOLU)) || detailIlan.GORSEL_YOLU ? null : (
                        <div
                          className="bg-light border rounded d-flex align-items-center justify-content-center text-muted mb-3"
                          style={{ height: 180 }}
                        >
                          <i className="bx bx-image display-4" />
                        </div>
                      )}
                      {eidsKapsaminda(detailIlan) && (
                        <div className="alert alert-danger py-2 px-3 font-size-12 mb-2">
                          <i className="bx bx-error me-1" />{EIDS_UYARI}
                        </div>
                      )}
                      <div className="d-flex flex-wrap gap-1 mb-2">
                        {statusBadge(detailIlan.AKTIF_DURUM)}
                        {detailIlan.IsPremium && <Badge color="warning">Premium</Badge>}
                        {detailIlan.IsBold && <Badge color="dark">Kalın</Badge>}
                        {detailIlan.IsColored && <Badge color="info">Renkli</Badge>}
                        {detailIlan.IsStory && <Badge style={{ background: "#7367f0" }}>Story</Badge>}
                        {detailIlan.SATIS_MI && <Badge color="primary">Satış</Badge>}
                        {detailIlan.TEKLIFE_ACIK_MI && <Badge color="success">Teklif Açık</Badge>}
                        {detailIlan.DEGERLEMEYE_ACIK_MI && <Badge color="secondary">Değerleme</Badge>}
                      </div>
                    </Col>
                    <Col md={8}>
                      <Row className="g-3">
                        {[
                          ["İlan Adı", detailIlan.AD],
                          ["Kategori", kategoriYolu(detailIlan.KATEGORI_ID).join(" › ") || detailIlan.KATEGORILER?.AD || "-"],
                          ["Satıcı", detailIlan.KULLANICILAR?.AD_SOYAD || "-"],
                          ["E-posta", detailIlan.KULLANICILAR?.Mail_Adress || "-"],
                          ["Para Birimi", normalizeCurrency(detailIlan.PARA_BIRIMI) === "EUR" ? "€ Euro" : "₺ TL"],
                          ["Fiyat", detailIlan.FIYAT != null && detailIlan.FIYAT !== "" ? formatIlanPrice(detailIlan.FIYAT, detailIlan.PARA_BIRIMI) : "-"],
                          ["İndirimli Fiyat", detailIlan.INDIRIMLI_FIYAT ? formatIlanPrice(detailIlan.INDIRIMLI_FIYAT, detailIlan.PARA_BIRIMI) : "-"],
                          ["Birim Fiyat", detailIlan.BIRIM_FIYAT ? `${formatIlanPrice(detailIlan.BIRIM_FIYAT, detailIlan.PARA_BIRIMI)} / ${detailIlan.URUN_BIRIMI || "birim"}` : "-"],
                          ["Stok", detailIlan.STOK_ADEDI ?? "-"],
                          ["Görüntülenme", detailIlan.GÖRÜNTÜLENME_SAYISI ?? 0],
                          ["Konum", [detailIlan.KONUM_IL, detailIlan.KONUM_ILCE].filter(Boolean).join(" / ") || "-"],
                          ["Popülerlik Etiketi", detailIlan.POPULERLIK_ETIKETI || "-"],
                          ["Oluşturma Tarihi", formatApiDateTime(detailIlan.OLUSTURMA_TARIHI)],
                          ["Güncelleme Tarihi", formatApiDateTime(detailIlan.GUNCELLEME_TARIHI)],
                          ["Bitiş Tarihi", formatApiDateTime(detailIlan.BITIS_TARIHI)],
                          ["Vitrin Bitiş Tarihi", !detailIlan.IsPremium
                            ? "-"
                            : parseUtc(detailIlan.VITRIN_BITIS_TARIHI)?.toLocaleString("tr-TR")
                              || "Süresiz"],
                        ].map(([label, value]) => (
                          <Col sm={6} key={label}>
                            <Label className="text-muted mb-0 font-size-11">{label}</Label>
                            <p className="fw-medium mb-0">{value}</p>
                          </Col>
                        ))}
                        {detailIlan.ACIKLAMA && (
                          <Col sm={12}>
                            <Label className="text-muted mb-1 font-size-11">Açıklama</Label>
                            <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{detailIlan.ACIKLAMA}</p>
                          </Col>
                        )}
                        {detailIlan.TESLIMAT && (
                          <Col sm={12}>
                            <Label className="text-muted mb-1 font-size-11">Teslimat Bilgisi</Label>
                            <p className="mb-0">{detailIlan.TESLIMAT}</p>
                          </Col>
                        )}
                      </Row>
                    </Col>
                  </Row>
                </TabPane>

                {/* Tab 2: Medya */}
                <TabPane tabId="2">
                  {ilanMedya.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-images display-4 mb-2 opacity-50" />
                      <p>Medya bulunamadı.</p>
                    </div>
                  ) : (
                    <Row className="g-3">
                      {ilanMedya.map((m, idx) => (
                        <Col key={idx} xs={6} md={4} lg={3}>
                          <Card className="border shadow-none h-100">
                            <div style={{ background: "#f8f9fa", height: 160, overflow: "hidden" }}>
                              {m.MEDYA_TIP === "video" ? (
                                <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                  <i className="bx bx-play-circle display-4" />
                                </div>
                              ) : (
                                <img
                                  src={m.MEDYA_URL || m.MEDYA_YOLU}
                                  alt={m.BASLIK || `Medya ${idx + 1}`}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              )}
                            </div>
                            <CardBody className="p-2">
                              <p className="mb-0 font-size-11 text-muted text-truncate">
                                {m.BASLIK || `Sıra: ${m.SIRA ?? idx + 1}`}
                              </p>
                              <Badge color="light" className="text-dark font-size-10 border">{m.MEDYA_TIP}</Badge>
                            </CardBody>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                </TabPane>

                {/* Tab 3: Teklifler */}
                <TabPane tabId="3">
                  {ilanTeklifler.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-message-square-dots display-4 mb-2 opacity-50" />
                      <p>Henüz teklif yok.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-hover align-middle mb-0" bordered>
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Kullanıcı</th>
                            <th>Teklif Tutarı</th>
                            <th>Tarih</th>
                            <th>Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ilanTeklifler.map(t => (
                            <tr key={t.ID}>
                              <td className="text-muted">#{t.ID}</td>
                              <td>
                                <div className="fw-medium">{t.KULLANICILAR?.AD_SOYAD || "-"}</div>
                                <small className="text-muted">{t.KULLANICILAR?.Mail_Adress}</small>
                              </td>
                              <td className="fw-bold text-primary">
                                {formatIlanPrice(t.TEKLIF_TUTARI, detailIlan?.PARA_BIRIMI)}
                              </td>
                              <td>
                                {t.TEKLIF_TARIHI
                                  ? formatApiDateTime(t.TEKLIF_TARIHI)
                                  : "-"}
                              </td>
                              <td>
                                <Badge color={teklifDurumColor(t.DURUM)}>
                                  {teklifDurumText(t.DURUM)}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </TabPane>

                {/* Tab 4: Değerlemeler */}
                <TabPane tabId="4">
                  {ilanDegerlemeler.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-bar-chart-alt-2 display-4 mb-2 opacity-50" />
                      <p>Değerleme bulunamadı.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-hover align-middle mb-0" bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Kullanıcı</th>
                            <th>Önerilen Fiyat</th>
                            <th>Tarih</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ilanDegerlemeler.map(d => (
                            <tr key={d.ID}>
                              <td>{d.KULLANICILAR?.AD_SOYAD || "-"}</td>
                              <td className="fw-bold text-success">
                                {formatIlanPrice(d.ONERILEN_FIYAT, detailIlan?.PARA_BIRIMI)}
                              </td>
                              <td>{formatApiDateTime(d.TARIH)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </TabPane>

                {/* Tab 5: Şikayetler */}
                <TabPane tabId="5">
                  {ilanSikayetler.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-shield-alt-2 display-4 mb-2 opacity-50" />
                      <p>Şikayet bulunamadı.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-hover align-middle mb-0" bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Kullanıcı</th>
                            <th>Şikayet Tipi</th>
                            <th>Açıklama</th>
                            <th>Tarih</th>
                            <th>Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ilanSikayetler.map(s => (
                            <tr key={s.ID}>
                              <td>{s.KULLANICILAR?.AD_SOYAD || "-"}</td>
                              <td><Badge color="warning">{s.SIKAYET_TIP}</Badge></td>
                              <td className="font-size-12" style={{ maxWidth: 200 }}>
                                <span className="text-truncate d-block">{s.ACIKLAMA || "-"}</span>
                              </td>
                              <td>{formatApiDate(s.TARIH)}</td>
                              <td>
                                <Badge color={s.DURUM === "çözüldü" ? "success" : "danger"}>
                                  {s.DURUM}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </TabPane>

                {/* Tab 6: Mesajlaşmalar (Firestore) */}
                <TabPane tabId="6">
                  {sohbetError && (
                    <Alert color="warning" className="border-0 d-flex align-items-center justify-content-between">
                      <span><i className="mdi mdi-alert-outline me-2" />{sohbetError}</span>
                      <Button
                        color="warning" size="sm" outline
                        onClick={() => { setSohbetError(""); loadSohbetler(detailIlan?.ID); }}
                      >
                        <i className="bx bx-refresh me-1" />Tekrar Dene
                      </Button>
                    </Alert>
                  )}
                  {sohbetLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Sohbetler yükleniyor...</p>
                    </div>
                  ) : ilanSohbetler.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-chat display-4 mb-2 opacity-50" />
                      <p>Bu ilana ait mesajlaşma bulunamadı.</p>
                    </div>
                  ) : (
                    <Row className="g-3">
                      {/* Sohbet listesi */}
                      <Col md={4}>
                        <div className="mb-2 position-relative">
                          <i className="bx bx-search position-absolute text-muted" style={{ left: 10, top: 9 }} />
                          <Input
                            bsSize="sm"
                            placeholder="Sohbet ara (isim/mesaj)..."
                            value={sohbetAra}
                            onChange={(e) => setSohbetAra(e.target.value)}
                            style={{ paddingLeft: 30 }}
                          />
                        </div>
                        <div className="border rounded" style={{ maxHeight: 430, overflowY: "auto" }}>
                          {ilanSohbetler
                            .filter((s) => {
                              if (!sohbetAra.trim()) return true;
                              const q = normTR(sohbetAra);
                              const names = Object.values(s.participants || {}).map(p => p?.name).join(" ");
                              return normTR(names).includes(q) || normTR(s.lastMessageText).includes(q);
                            })
                            .map((s) => {
                            const parts = Object.values(s.participants || {});
                            const isim = parts.map(p => p?.name).filter(Boolean).join(" ↔ ") || "Sohbet";
                            const aktif = aktifSohbet?.id === s.id;
                            return (
                              <div
                                key={s.id}
                                onClick={() => openSohbet(s)}
                                className={`p-2 border-bottom ${aktif ? "bg-light" : ""}`}
                                style={{ cursor: "pointer" }}
                              >
                                <div className="fw-medium font-size-13 text-truncate">
                                  <i className="bx bx-user-circle me-1 text-muted" />{isim}
                                </div>
                                <div className="text-muted font-size-11 text-truncate">
                                  {s.lastMessageText || "—"}
                                </div>
                                <div className="text-muted font-size-10">
                                  {s.lastMessageAt || ""}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </Col>
                      {/* Mesaj akışı */}
                      <Col md={8}>
                        {!aktifSohbet ? (
                          <div className="text-center py-5 text-muted border rounded h-100 d-flex flex-column justify-content-center">
                            <i className="bx bx-message-rounded-dots display-4 mb-2 opacity-50" />
                            <p className="mb-0">Görüntülemek için soldan bir sohbet seçin.</p>
                          </div>
                        ) : mesajLoading ? (
                          <div className="text-center py-5"><Spinner color="primary" /></div>
                        ) : (
                          <>
                            <div className="mb-2 position-relative">
                              <i className="bx bx-search-alt position-absolute text-muted" style={{ left: 10, top: 9 }} />
                              <Input
                                bsSize="sm"
                                placeholder="Bu sohbette mesaj ara..."
                                value={mesajAra}
                                onChange={(e) => setMesajAra(e.target.value)}
                                style={{ paddingLeft: 30 }}
                              />
                              {mesajAra && (
                                <small className="text-muted position-absolute" style={{ right: 10, top: 8 }}>
                                  {sohbetMesajlar.filter(m => normTR(m.customerMessage).includes(normTR(mesajAra))).length} eşleşme
                                </small>
                              )}
                            </div>
                          <div
                            className="border rounded p-3"
                            style={{ maxHeight: 420, overflowY: "auto", background: "#f5f6f8" }}
                          >
                            {sohbetMesajlar.length === 0 ? (
                              <div className="text-center text-muted py-4">Mesaj yok.</div>
                            ) : (
                              sohbetMesajlar
                                .filter((m) => !mesajAra.trim() || normTR(m.customerMessage).includes(normTR(mesajAra)))
                                .map((m) => {
                                // Gönderen ID mobil uygulamada `customerNumber` alanında tutulur.
                                const senderUid = m.customerNumber ?? m.senderId ?? m.customerId ?? "";
                                // İlk katılımcı sağda, diğeri solda konumlanır.
                                const ids = aktifSohbet.participantIds || Object.keys(aktifSohbet.participants || {});
                                const sag = String(senderUid) === String(ids[0]);
                                const gonderen =
                                  aktifSohbet.participants?.[senderUid]?.name ||
                                  (senderUid ? `#${senderUid}` : "Bilinmeyen");
                                return (
                                  <div
                                    key={m.id}
                                    className={`d-flex mb-2 ${sag ? "justify-content-end" : "justify-content-start"}`}
                                  >
                                    <div
                                      className={`p-2 rounded shadow-sm ${sag ? "bg-primary text-white" : "bg-white border"}`}
                                      style={{ maxWidth: "75%" }}
                                    >
                                      <div className={`font-size-10 mb-1 ${sag ? "text-white-50" : "text-muted"}`}>
                                        {gonderen}
                                      </div>
                                      {m.type === "image" ? (
                                        <img
                                          src={m.customerMessage}
                                          alt="mesaj görseli"
                                          style={{ maxWidth: "100%", borderRadius: 6 }}
                                        />
                                      ) : m.type === "location" ? (
                                        <a
                                          href={`https://maps.google.com/?q=${m.lat},${m.lng}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          className={sag ? "text-white" : ""}
                                        >
                                          <i className="bx bx-map me-1" />Konumu Görüntüle
                                        </a>
                                      ) : (
                                        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                          {m.customerMessage}
                                        </div>
                                      )}
                                      <div className={`font-size-10 mt-1 text-end ${sag ? "text-white-50" : "text-muted"}`}>
                                        {m.messageDate}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                          </>
                        )}
                      </Col>
                    </Row>
                  )}
                </TabPane>
              </TabContent>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary" outline
            onClick={() => {
              setDetailModal(false);
              setEditIlan({ ...detailIlan });
              setEditModal(true);
            }}
          >
            <i className="bx bx-pencil me-1" />Düzenle
          </Button>
          <Button color="secondary" outline onClick={() => setDetailModal(false)}>Kapat</Button>
        </ModalFooter>
      </Modal>

      {/* ══ HIZLI DÜZENLEME MODAL ════════════════════════════════ */}
      <Modal isOpen={editModal} toggle={() => setEditModal(false)} centered size="lg" scrollable>
        <ModalHeader toggle={() => setEditModal(false)} className="bg-light">
          İlan Düzenle — <span className="text-primary">#{editIlan?.ID}</span>
        </ModalHeader>
        <ModalBody>
          {editIlan && (
            <Row className="g-3">
              {/* ── TEMEL BİLGİLER ── */}
              <Col sm={12}>
                <h6 className="text-primary fw-bold mb-0"><i className="bx bx-info-circle me-1" />Temel Bilgiler</h6>
              </Col>
              <Col sm={8}>
                <FormGroup className="mb-0">
                  <Label>İlan Adı</Label>
                  <Input
                    type="text"
                    value={editIlan.AD || ""}
                    onChange={e => setEditIlan({ ...editIlan, AD: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={4}>
                <FormGroup className="mb-0 position-relative">
                  <Label>Kategori</Label>
                  {/* Aranabilir kategori seçici — üst › alt hiyerarşisini gösterir */}
                  <Input
                    type="text"
                    autoComplete="off"
                    placeholder="Kategori ara (üst › alt)…"
                    value={
                      editKatDropdown
                        ? editKatSearch
                        : (editIlan.KATEGORI_ID
                            ? kategoriYolu(editIlan.KATEGORI_ID).join(" › ") ||
                              kategoriMap[editIlan.KATEGORI_ID]?.AD || ""
                            : "")
                    }
                    onFocus={() => { setEditKatDropdown(true); setEditKatSearch(""); }}
                    onChange={e => { setEditKatSearch(e.target.value); setEditKatDropdown(true); }}
                    onBlur={() => setTimeout(() => setEditKatDropdown(false), 150)}
                  />
                  {editIlan.KATEGORI_ID && (
                    <div className="text-muted font-size-11 mt-1">
                      <i className="bx bx-sitemap me-1" />
                      {(() => {
                        const yol = kategoriYolu(editIlan.KATEGORI_ID);
                        if (yol.length <= 1) return <Badge color="secondary">Ana Kategori</Badge>;
                        return yol.map((ad, idx) => (
                          <span key={idx}>
                            <Badge color={idx === yol.length - 1 ? "primary" : "light"}
                              className={idx === yol.length - 1 ? "" : "text-dark border"}>
                              {ad}
                            </Badge>
                            {idx < yol.length - 1 && <i className="bx bx-chevron-right mx-1" />}
                          </span>
                        ));
                      })()}
                    </div>
                  )}
                  {editIlan.KATEGORI_ID && ustKategoriIdleri.has(Number(editIlan.KATEGORI_ID)) && (
                    <div className="text-warning font-size-11 mt-1">
                      <i className="bx bx-error me-1" />
                      Bu kategorinin alt kategorileri var; ilan en alt seviyeye (ör. model) taşınmalı.
                    </div>
                  )}
                  {editKatDropdown && (
                    <div
                      className="border rounded bg-white shadow-sm position-absolute w-100"
                      style={{ zIndex: 1000, maxHeight: 260, overflowY: "auto", top: "100%" }}
                    >
                      {filtrelenmisKategoriler.length === 0 ? (
                        <div className="p-2 text-muted font-size-12">Kategori bulunamadı.</div>
                      ) : (
                        filtrelenmisKategoriler.map(k => (
                          <button
                            type="button"
                            key={k.id}
                            className={`d-block w-100 text-start border-0 bg-transparent px-2 py-1 font-size-12 ${String(editIlan.KATEGORI_ID) === String(k.id) ? "bg-light fw-semibold" : ""}`}
                            style={{ cursor: "pointer" }}
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => {
                              setEditIlan({ ...editIlan, KATEGORI_ID: k.id });
                              setEditKatDropdown(false);
                              setEditKatSearch("");
                            }}
                          >
                            {k.seviye > 0 && (
                              <span className="text-muted">{"— ".repeat(k.seviye)}</span>
                            )}
                            {k.yol.length > 1 ? (
                              <>
                                <span className="text-muted">{k.yol.slice(0, -1).join(" › ")} › </span>
                                <span>{k.ad}</span>
                              </>
                            ) : (
                              <span>{k.ad} <Badge color="light" className="text-dark border font-size-10">Ana</Badge></span>
                            )}
                            {ustKategoriIdleri.has(Number(k.id)) && (
                              <Badge color="warning" className="ms-1 font-size-10" title="Bu kategorinin alt kategorileri var">
                                alt kategorili
                              </Badge>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col sm={12}>
                <FormGroup className="mb-0">
                  <Label>Açıklama</Label>
                  <Input
                    type="textarea"
                    rows={3}
                    value={editIlan.ACIKLAMA || ""}
                    onChange={e => setEditIlan({ ...editIlan, ACIKLAMA: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={8}>
                <FormGroup className="mb-0">
                  <Label>Görsel URL</Label>
                  <Input
                    type="text"
                    value={editIlan.GORSEL_YOLU || ""}
                    onChange={e => setEditIlan({ ...editIlan, GORSEL_YOLU: e.target.value })}
                    placeholder="https://..."
                  />
                </FormGroup>
              </Col>
              <Col sm={4}>
                <FormGroup className="mb-0">
                  <Label>Popülerlik Etiketi</Label>
                  <Input
                    type="text"
                    value={editIlan.POPULERLIK_ETIKETI || ""}
                    onChange={e => setEditIlan({ ...editIlan, POPULERLIK_ETIKETI: e.target.value })}
                    placeholder="ör. Çok Satan"
                  />
                </FormGroup>
              </Col>

              {/* ── FİYAT / STOK ── */}
              <Col sm={12} className="mt-3">
                <h6 className="text-primary fw-bold mb-0"><i className="bx bx-purchase-tag me-1" />Fiyat & Stok</h6>
              </Col>
              <Col sm={4}>
                <FormGroup className="mb-0">
                  <Label>Para Birimi</Label>
                  <Input
                    type="select"
                    value={normalizeCurrency(editIlan.PARA_BIRIMI)}
                    onChange={e => setEditIlan({ ...editIlan, PARA_BIRIMI: e.target.value })}
                  >
                    <option value="TRY">₺ TL (TRY)</option>
                    <option value="EUR">€ Euro (EUR)</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col sm={4}>
                <FormGroup className="mb-0">
                  <Label>Fiyat ({currencySymbol(editIlan.PARA_BIRIMI)})</Label>
                  <Input
                    type="number"
                    value={editIlan.FIYAT ?? ""}
                    onChange={e => setEditIlan({ ...editIlan, FIYAT: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={4}>
                <FormGroup className="mb-0">
                  <Label>İndirimli Fiyat ({currencySymbol(editIlan.PARA_BIRIMI)})</Label>
                  <Input
                    type="number"
                    value={editIlan.INDIRIMLI_FIYAT ?? ""}
                    onChange={e => setEditIlan({ ...editIlan, INDIRIMLI_FIYAT: e.target.value })}
                    placeholder="Boş = indirim yok"
                  />
                </FormGroup>
              </Col>
              <Col sm={4}>
                <FormGroup className="mb-0">
                  <Label>Birim Fiyat ({currencySymbol(editIlan.PARA_BIRIMI)})</Label>
                  <Input
                    type="number"
                    value={editIlan.BIRIM_FIYAT ?? ""}
                    onChange={e => setEditIlan({ ...editIlan, BIRIM_FIYAT: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={4}>
                <FormGroup className="mb-0">
                  <Label>Ürün Birimi</Label>
                  <Input
                    type="text"
                    value={editIlan.URUN_BIRIMI || ""}
                    onChange={e => setEditIlan({ ...editIlan, URUN_BIRIMI: e.target.value })}
                    placeholder="kg, adet, ..."
                  />
                </FormGroup>
              </Col>
              <Col sm={4}>
                <FormGroup className="mb-0">
                  <Label>Stok Adedi</Label>
                  <Input
                    type="number"
                    value={editIlan.STOK_ADEDI ?? ""}
                    onChange={e => setEditIlan({ ...editIlan, STOK_ADEDI: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={4}>
                <FormGroup className="mb-0">
                  <Label>Kargo Ücreti (₺)</Label>
                  <Input
                    type="number"
                    value={editIlan.KARGO_UCRETI ?? ""}
                    onChange={e => setEditIlan({ ...editIlan, KARGO_UCRETI: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={6}>
                <FormGroup className="mb-0">
                  <Label>Canlı Kilo</Label>
                  <Input
                    type="number"
                    value={editIlan.CANLI_KILO ?? ""}
                    onChange={e => setEditIlan({ ...editIlan, CANLI_KILO: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={6}>
                <FormGroup className="mb-0">
                  <Label>Karkas Kilo</Label>
                  <Input
                    type="number"
                    value={editIlan.KARKAS_KILO ?? ""}
                    onChange={e => setEditIlan({ ...editIlan, KARKAS_KILO: e.target.value })}
                  />
                </FormGroup>
              </Col>

              {/* ── KONUM / TESLİMAT ── */}
              <Col sm={12} className="mt-3">
                <h6 className="text-primary fw-bold mb-0"><i className="bx bx-map-pin me-1" />Konum & Teslimat</h6>
              </Col>
              <Col sm={4}>
                <FormGroup className="mb-0">
                  <Label>İl</Label>
                  <Input
                    type="text"
                    value={editIlan.KONUM_IL || ""}
                    onChange={e => setEditIlan({ ...editIlan, KONUM_IL: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={4}>
                <FormGroup className="mb-0">
                  <Label>İlçe</Label>
                  <Input
                    type="text"
                    value={editIlan.KONUM_ILCE || ""}
                    onChange={e => setEditIlan({ ...editIlan, KONUM_ILCE: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={4}>
                <FormGroup className="mb-0">
                  <Label>Teslimat</Label>
                  <Input
                    type="text"
                    value={editIlan.TESLIMAT || ""}
                    onChange={e => setEditIlan({ ...editIlan, TESLIMAT: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={12}>
                <FormGroup className="mb-0">
                  <Label>Adres Detayı</Label>
                  <Input
                    type="text"
                    value={editIlan.KONUM_ADRES_DETAY || ""}
                    onChange={e => setEditIlan({ ...editIlan, KONUM_ADRES_DETAY: e.target.value })}
                  />
                </FormGroup>
              </Col>

              {/* ── DURUM / TARİH ── */}
              <Col sm={12} className="mt-3">
                <h6 className="text-primary fw-bold mb-0"><i className="bx bx-toggle-left me-1" />Durum & Tarih</h6>
              </Col>
              <Col sm={6}>
                <FormGroup className="mb-0">
                  <Label>Durum</Label>
                  <Input
                    type="select"
                    value={editIlan.AKTIF_DURUM || ""}
                    onChange={e => setEditIlan({ ...editIlan, AKTIF_DURUM: e.target.value })}
                  >
                    <option value="Aktif">Aktif</option>
                    <option value="Pasif">Pasif</option>
                    <option value="Beklemede">Beklemede</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col sm={6}>
                <FormGroup className="mb-0">
                  <Label>Bitiş Tarihi</Label>
                  <Input
                    type="datetime-local"
                    value={editIlan.BITIS_TARIHI
                      ? (() => {
                          // API değeri -> datetime-local (yerel saat).
                          // parseApiDate: API'nin yanlış (+03:00) etiketini atıp
                          // değeri UTC okur; aksi halde alan 3 saat geriden dolar.
                          const d = parseApiDate(editIlan.BITIS_TARIHI);
                          if (!d) return "";
                          const offset = d.getTimezoneOffset(); // dakika
                          const local = new Date(d.getTime() - offset * 60000);
                          return local.toISOString().slice(0, 16);
                        })()
                      : ""}
                    onChange={e => {
                      const val = e.target.value;
                      if (!val) {
                        setEditIlan({ ...editIlan, BITIS_TARIHI: null });
                        return;
                      }
                      // datetime-local (yerel saat) -> UTC ISO string
                      const local = new Date(val);
                      setEditIlan({ ...editIlan, BITIS_TARIHI: local.toISOString() });
                    }}
                  />
                </FormGroup>
              </Col>

              {/* ── VİTRİN (PREMIUM) SÜRESİ ── */}
              <Col sm={12} className="mt-3">
                <div className="bg-light p-3 rounded border">
                  <Label className="fw-semibold mb-2 d-block">
                    Vitrin (Premium) Süresi
                  </Label>
                  <Row className="align-items-end">
                    <Col sm={6}>
                      <FormGroup className="mb-0">
                        <Label className="font-size-12 text-muted">
                          Vitrin Bitiş Tarihi
                        </Label>
                        <Input
                          type="datetime-local"
                          disabled={!editIlan.IsPremium}
                          value={(() => {
                            const d = parseUtc(editIlan.VITRIN_BITIS_TARIHI);
                            if (!d) return "";
                            const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
                            return local.toISOString().slice(0, 16);
                          })()}
                          onChange={e => {
                            const val = e.target.value;
                            if (!val) {
                              setEditIlan({ ...editIlan, VITRIN_BITIS_TARIHI: null });
                              return;
                            }
                            const local = new Date(val);
                            setEditIlan({ ...editIlan, VITRIN_BITIS_TARIHI: local.toISOString() });
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col sm={6}>
                      <div className="d-flex flex-wrap gap-1">
                        {[
                          { label: "7 gün", gun: 7 },
                          { label: "15 gün", gun: 15 },
                          { label: "30 gün", gun: 30 },
                        ].map(x => (
                          <Button
                            key={x.gun}
                            size="sm"
                            color="warning"
                            outline
                            disabled={!editIlan.IsPremium}
                            onClick={() =>
                              setEditIlan({
                                ...editIlan,
                                VITRIN_BITIS_TARIHI: new Date(
                                  Date.now() + x.gun * 24 * 60 * 60 * 1000,
                                ).toISOString(),
                              })
                            }
                          >
                            +{x.label}
                          </Button>
                        ))}
                        <Button
                          size="sm"
                          color="secondary"
                          outline
                          disabled={!editIlan.IsPremium}
                          onClick={() => setEditIlan({ ...editIlan, VITRIN_BITIS_TARIHI: null })}
                        >
                          Temizle
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  {/* Vitrin sorgusu (HomeScreen) "VITRIN_BITIS_TARIHI eq null" kayıtları da
                      getirir — boş bırakılırsa ilan vitrinden hiç düşmez. */}
                  {editIlan.IsPremium && !editIlan.VITRIN_BITIS_TARIHI && (
                    <div className="text-danger font-size-11 mt-2">
                      <i className="mdi mdi-alert-outline me-1" />
                      Tarih boş: ilan <strong>süresiz</strong> vitrinde kalır. Süre vermek için
                      yukarıdan bir tarih seçin.
                    </div>
                  )}
                  {!editIlan.IsPremium && (
                    <div className="text-muted font-size-11 mt-2">
                      Süre girmek için önce aşağıdan <strong>Premium</strong> etiketini açın.
                    </div>
                  )}
                </div>
              </Col>

              {/* ── ETİKETLER ── */}
              <Col sm={12} className="mt-3">
                <div className="bg-light p-3 rounded border">
                  <Label className="fw-semibold mb-2 d-block">Etiketler & Özellikler</Label>
                  <Row>
                    {[
                      { key: "IsPremium", label: "Premium" },
                      { key: "IsBold", label: "Kalın Yazı" },
                      { key: "IsColored", label: "Renkli" },
                      { key: "IsStory", label: "Story" },
                      { key: "SATIS_MI", label: "Satış" },
                      { key: "TEKLIFE_ACIK_MI", label: "Teklif Açık" },
                      { key: "DEGERLEMEYE_ACIK_MI", label: "Değerlemeye Açık" },
                    ].map(sw => (
                      <Col xs={6} sm={4} key={sw.key}>
                        <FormGroup switch className="mb-2">
                          <Input
                            type="switch"
                            checked={!!editIlan[sw.key]}
                            onChange={e => setEditIlan({ ...editIlan, [sw.key]: e.target.checked })}
                          />
                          <Label check>{sw.label}</Label>
                        </FormGroup>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setEditModal(false)}>İptal</Button>
          <Button color="primary" onClick={handleEditSave} disabled={editLoading}>
            {editLoading ? <Spinner size="sm" /> : <i className="bx bx-check me-1" />}
            Kaydet
          </Button>
        </ModalFooter>
      </Modal>

      <KullaniciIletisimModal
        isOpen={!!iletisimIlan}
        toggle={() => setIletisimIlan(null)}
        tip={iletisimIlan?.DEGERLEMEYE_ACIK_MI ? "DEGERLEME" : "ILAN"}
        ilgiliId={iletisimIlan?.ID}
        ilgiliBaslik={iletisimIlan?.AD}
        kullanici={iletisimIlan ? { ID: iletisimIlan.KIMDEN_ID, ...(iletisimIlan.KULLANICILAR || {}) } : null}
      />
    </React.Fragment>
  );
};

export default IlanYonetimi;
